import { useParams } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  User,
  History,
  Plus,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Edit,
  Download,
  FileUp,
  Users,
  Shield,
  FileText,
  Clock,
  UserX,
  X,
  Upload,
  Trash2,
} from "lucide-react";
import { Link, useLocation } from "wouter";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EMPLOYMENT_TYPES } from "@/lib/usStates";
import { AddressFields } from "@/components/forms/AddressFields";
import { composeAddressLine, defaultCountry, emptyAddressFields, normalizeAddressFields } from "@/lib/address";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertEmployeeSchema, contactInteractionFormSchema } from "@shared/schema";
import { apiRequest } from "@/lib/queryClient";
import { coverageTypeLabel } from "@/lib/coverageLabel";
import { formatPhoneInputValue } from "@/lib/phone";
import { useToast } from "@/hooks/use-toast";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  formatDate,
  formatDateDisplay,
  formatSource,
  SOURCE_OPTIONS,
  OUTCOME_LABELS,
  formatDateLong,
  getCurrentLocalDateTime,
  formatCurrency,
} from "../../lib/utils";
import { calendarService } from "@/lib/calendarService";
import { exportToCSV, downloadEmployeeImportTemplate } from "@/lib/exportUtils";
import { applyFullSsns, fetchFullSsns } from "@/lib/ssnReveal";
import { useDocuments } from "@/hooks/useDocuments";
import { useDocumentFolders } from "@/hooks/useDocumentFolders";
import { DocumentList } from "@/components/ui/document-list";
import { DocumentUpload } from "@/components/ui/document-upload";
import { Label } from "@/components/ui/label";
import ProspectOrganizationTab from "./ProspectOrganizationTab";
import AgencyRepresentatives from "@/components/AgencyRepresentatives";
import { EmployeeDefaultSettingsCard } from "@/components/EmployeeDefaultSettingsCard";
import { useEmployeeDefaultSettings } from "@/hooks/useEmployeeDefaultSettings";
import { COMPENSATION_TYPE_OPTIONS, PAY_CYCLE_OPTIONS } from "@/lib/employeeDefaults";
import { OrganizationSelect } from "@/components/forms/OrganizationSelect";
import { ProfilePictureUpload } from "@/components/ui/profile-picture-upload";
import { sortEmployeesByFullName, buildNameSortKey } from "@/lib/employeeSort";
import { SIC_CODES, formatSicCode } from "@shared/sicCodes";
import { formatEINInputValue } from "@/lib/ein";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn, parseDateLocal } from "@/lib/utils";

function getQueryParamFromLocation(location: string, key: string): string | null {
  const questionMarkIndex = location.indexOf("?");
  if (questionMarkIndex < 0) return null;
  const search = location.slice(questionMarkIndex + 1);
  const params = new URLSearchParams(search);
  return params.get(key);
}

function getQueryParamFromBrowserUrl(key: string): string | null {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  return params.get(key);
}

function setQueryParamOnLocation(location: string, key: string, value: string): string {
  const questionMarkIndex = location.indexOf("?");
  const path = questionMarkIndex >= 0 ? location.slice(0, questionMarkIndex) : location;
  const existingSearch = questionMarkIndex >= 0 ? location.slice(questionMarkIndex + 1) : "";
  const params = new URLSearchParams(existingSearch);
  params.set(key, value);
  const nextSearch = params.toString();
  return nextSearch ? `${path}?${nextSearch}` : path;
}

function ProspectDocumentsTab({ prospectId }: { prospectId: number }) {
  const ROOT_FOLDER_VALUE = "root";
  const [selectedFolderId, setSelectedFolderId] = React.useState<number | null>(
    null
  );
  const [newFolderName, setNewFolderName] = React.useState("");
  const [isCreateFolderOpen, setIsCreateFolderOpen] = React.useState(false);
  const [isSavingFolder, setIsSavingFolder] = React.useState(false);
  const { toast } = useToast();

  const { documents, loading, error, refreshDocuments } = useDocuments({
    autoRefresh: true,
    ownerType: "prospect",
    prospectId: prospectId,
    folderId:
      selectedFolderId && selectedFolderId > 0 ? selectedFolderId : undefined,
    unfiled: selectedFolderId === null,
  });
  const { folders, createFolder, refreshFolders } = useDocumentFolders({
    ownerType: "prospect",
    prospectId,
  });

  const moveToFolder = async (documentId: number, folderId: number | null) => {
    const res = await fetch(`/api/documents/${documentId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ folderId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || "Failed to move");
    }
    await refreshDocuments();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || isSavingFolder) return;
    try {
      setIsSavingFolder(true);
      await createFolder(newFolderName.trim());
      await refreshFolders();
      toast({ title: "Folder created", variant: "success" });
      setIsCreateFolderOpen(false);
      setNewFolderName("");
    } catch (e: any) {
      toast({
        title: "Create failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setIsSavingFolder(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm">Folder</Label>
            <Select
              value={
                selectedFolderId === null
                  ? ROOT_FOLDER_VALUE
                  : String(selectedFolderId)
              }
              onValueChange={(v) => {
                if (v === ROOT_FOLDER_VALUE) setSelectedFolderId(null);
                else setSelectedFolderId(parseInt(v, 10));
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Root folder" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ROOT_FOLDER_VALUE}>Root folder</SelectItem>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCreateFolderOpen(true)}
            >
              New Folder
            </Button>
            <DocumentUpload
              trigger={
                <Button size="sm">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload
                </Button>
              }
              onUploadSuccess={() => {
                refreshDocuments();
                toast({ title: "Document uploaded", variant: "success" });
              }}
              ownerType="prospect"
              prospectId={prospectId}
              folderId={
                selectedFolderId && selectedFolderId > 0
                  ? selectedFolderId
                  : undefined
              }
            />
          </div>
        </div>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">
            Error loading documents: {error}
          </p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      ) : (
        <DocumentList
          documents={documents as any}
          onDocumentUpdate={refreshDocuments}
          showActions={true}
          folders={folders}
          onMoveToFolder={moveToFolder}
        />
      )}

      <Dialog
        open={isCreateFolderOpen}
        onOpenChange={(open) => {
          setIsCreateFolderOpen(open);
          if (!open) {
            setNewFolderName("");
            setIsSavingFolder(false);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Create Folder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="prospect-new-folder-name">Folder name</Label>
            <Input
              id="prospect-new-folder-name"
              placeholder="e.g. Contracts"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsCreateFolderOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleCreateFolder}
              disabled={!newFolderName.trim() || isSavingFolder}
            >
              {isSavingFolder ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

function ProspectAsyncDependentList({ employeeId }: { employeeId: number }) {
  // Deprecated in favor of ProspectAsyncDependentRows (kept temporarily to avoid
  // large refactors in unrelated sections). This component is no longer used.
  return null;
}

function ProspectAsyncDependentRows({
  employeeId,
  plan,
  enrollmentId,
}: {
  employeeId: number;
  plan: any;
  enrollmentId: number;
}) {
  const [list, setList] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!employeeId) return;
      setLoading(true);
      try {
        const res = await fetch(`/api/employees/${employeeId}/dependents`);
        const deps = res.ok ? await res.json() : [];
        if (!mounted) return;
        setList(Array.isArray(deps) ? deps : []);
      } catch {
        if (!mounted) return;
        setList([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [employeeId]);

  const calcAge = (dob?: string | Date | null) => {
    if (!dob) return 0;
    const d = parseDateLocal(dob as any);
    if (!d || Number.isNaN(d.getTime())) return 0;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return Math.max(0, age);
  };

  if (loading) {
    return (
      <tr className="border-b bg-muted/25">
        <td className="px-4 py-3 text-sm" colSpan={7}>
          <div className="pl-10 text-sm text-muted-foreground italic">
            Loading…
          </div>
        </td>
      </tr>
    );
  }

  if (!Array.isArray(list) || list.length === 0) {
    return (
      <tr className="border-b bg-muted/25">
        <td className="px-4 py-3 text-sm" colSpan={7}>
          <div className="pl-10 text-sm text-muted-foreground italic">
            No dependents on file
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>
      {list.map((d: any) => {
        const age = calcAge(d?.dateOfBirth);
        return (
          <tr
            key={`dep-${enrollmentId}-${d.id}`}
            className="border-b bg-muted/25"
          >
            <td className="px-4 py-3 text-sm">
              <div className="flex items-center gap-3 pl-10">
                <div className="h-8 w-8 rounded-full bg-background border flex items-center justify-center">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="font-medium truncate">
                    {d.firstName} {d.lastName}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {d.relationship && (
                      <Badge
                        variant="secondary"
                        className="h-5 px-2 capitalize"
                      >
                        {String(d.relationship)}
                      </Badge>
                    )}
                    <span>Age: {age}</span>
                  </div>
                </div>
              </div>
            </td>
            <td className="px-4 py-3 text-sm">
              <div className="flex flex-col gap-1">
                <span className="font-medium">{plan?.name || "—"}</span>
                <span className="text-xs text-muted-foreground">
                  {plan?.carrier || ""}
                </span>
              </div>
            </td>
            <td className="px-4 py-3 text-sm">
              <Badge
                variant="outline"
                className="capitalize bg-background border-border text-foreground shadow-sm"
              >
                Dependent
              </Badge>
            </td>
            <td className="px-4 py-3 text-right text-sm">
              <div className="flex flex-col items-end">
                <span className="font-medium">{formatCurrency(0)}</span>
                <span className="text-xs text-muted-foreground">per month</span>
              </div>
            </td>
            <td className="px-4 py-3 text-right text-sm">
              <div className="flex flex-col items-end">
                <span className="font-medium">{formatCurrency(0)}</span>
                <span className="text-xs text-muted-foreground">per month</span>
              </div>
            </td>
            <td className="px-4 py-3 text-right text-sm">
              <div className="flex flex-col items-end">
                <span className="font-medium">{formatCurrency(0)}</span>
                <span className="text-xs text-muted-foreground">
                  per pay period
                </span>
              </div>
            </td>
            <td className="px-4 py-3 text-right text-sm">
              <div className="flex items-center justify-end gap-2">
                <Button variant="outline" size="sm" disabled>
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive border-destructive/40"
                  disabled
                >
                  Remove
                </Button>
              </div>
            </td>
          </tr>
        );
      })}
    </>
  );
}

function getSavedProspectEnrollmentsPageSize(
  prospectId: string,
  fallback = 50
): number {
  try {
    if (typeof window === "undefined") return fallback;
    const raw = window.localStorage.getItem(
      `administr.prospect.enrollments.pageSize:${prospectId}`
    );
    const n = Number.parseInt(String(raw ?? ""), 10);
    const allowed = new Set([25, 50, 100, 200]);
    return Number.isFinite(n) && allowed.has(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function saveProspectEnrollmentsPageSize(prospectId: string, pageSize: number) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(
      `administr.prospect.enrollments.pageSize:${prospectId}`,
      String(pageSize)
    );
  } catch {}
}

export default function ProspectDetail() {
  const { id } = useParams<{ id: string }>();
  const prospectId = parseInt(id!, 10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isEditEmployeeDialogOpen, setIsEditEmployeeDialogOpen] =
    useState(false);
  const [isAddPlanDialogOpen, setIsAddPlanDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isConvertConfirmOpen, setIsConvertConfirmOpen] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  type EmployeeSortField = 'name' | 'status' | 'hireDate' | 'department' | 'position' | 'email' | 'phone';
  const [empSortField, setEmpSortField] = useState<EmployeeSortField | null>(null);
  const [empSortDirection, setEmpSortDirection] = useState<'asc' | 'desc'>('asc');
  const [empNameSortBasis, setEmpNameSortBasis] = useState<'firstName' | 'lastName'>('firstName');

  const handleEmployeeSort = (field: EmployeeSortField) => {
    if (empSortField === field) {
      setEmpSortDirection(empSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setEmpSortField(field);
      setEmpSortDirection('asc');
    }
  };

  const getEmpSortIcon = (field: EmployeeSortField) => {
    if (empSortField !== field) return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    return empSortDirection === 'asc' ? <ChevronUp className="h-4 w-4 ml-1" /> : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  const handleConfirmConvert = async () => {
    setIsConverting(true);
    try {
      const res = await fetch(`/api/prospects/${prospectId}/convert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deleteProspect: false }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Failed with ${res.status}`);
      }
      const data = await res.json();
      toast({
        title: "Converted to client",
        description:
          "Send the employer login from the client's Logins tab when ready.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/email/contacts"] });
      setIsConvertConfirmOpen(false);
      setLocation(`/clients/${data.clientId}`);
    } catch (e: any) {
      console.error("Convert error:", e);
      toast({
        title: "Conversion failed",
        description: e.message,
        variant: "destructive",
      });
    } finally {
      setIsConverting(false);
    }
  };
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvText, setCsvText] = useState<string>("");
  const [importPreview, setImportPreview] = useState<any[]>([]);
  const [isProcessingCsv, setIsProcessingCsv] = useState(false);
  const [isOrgApprovalOpen, setIsOrgApprovalOpen] = useState(false);
  const [pendingOrgItems, setPendingOrgItems] = useState<{
    jobTitles: string[];
    departments: string[];
    locations: string[];
    employeeClasses: string[];
    divisions: string[];
  }>({ jobTitles: [], departments: [], locations: [], employeeClasses: [], divisions: [] });
  const [isMissingDataDialogOpen, setIsMissingDataDialogOpen] = useState(false);
  const [skippedEmployees, setSkippedEmployees] = useState<Array<{
    rowNumber: number;
    name: string;
    email?: string;
    missingFields: string[];
    data?: any;
  }>>([]);
  const [location, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const allowedTabs = useMemo(
    () =>
      new Set([
        "profile",
        "employees",
        "benefits",
        "quotes",
        "contact-history",
        "documents",
        "organization",
      ]),
    []
  );

  const [activeTab, setActiveTab] = useState<string>(() => {
    const tabParam = getQueryParamFromBrowserUrl("tab") ?? getQueryParamFromLocation(location, "tab");
    return tabParam && allowedTabs.has(tabParam) ? tabParam : "profile";
  });

  useEffect(() => {
    const tabParam = getQueryParamFromBrowserUrl("tab") ?? getQueryParamFromLocation(location, "tab");
    const nextTab = tabParam && allowedTabs.has(tabParam) ? tabParam : "profile";
    setActiveTab(nextTab);
  }, [allowedTabs, location]);

  const handleTabChange = useCallback(
    (nextTab: string) => {
      const safeTab = allowedTabs.has(nextTab) ? nextTab : "profile";
      setActiveTab(safeTab);
      const currentUrl =
        typeof window !== "undefined"
          ? `${window.location.pathname}${window.location.search}`
          : location;
      setLocation(setQueryParamOnLocation(currentUrl, "tab", safeTab));
    },
    [allowedTabs, location, setLocation]
  );
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");

  // Benefits > Enrollments UI state (prospects) — match Client EnrollmentsTab UI
  const [isEnrollmentDialogOpen, setIsEnrollmentDialogOpen] = useState(false);
  const [enrollEmployeeOpen, setEnrollEmployeeOpen] = useState(false);
  const [enrollPlanOpen, setEnrollPlanOpen] = useState(false);
  const [enrollSelectedEmployeeId, setEnrollSelectedEmployeeId] = useState<
    number | null
  >(null);
  const [enrollSelectedPlanId, setEnrollSelectedPlanId] = useState<
    number | null
  >(null);
  const [enrollmentExpandedRows, setEnrollmentExpandedRows] = useState<
    Record<number, boolean>
  >({});
  const [enrollmentCoverageTab, setEnrollmentCoverageTab] =
    useState<string>("");
  const [enrollmentPageByTab, setEnrollmentPageByTab] = useState<
    Record<string, number>
  >({});
  const [enrollmentPageSize, setEnrollmentPageSize] = useState<number>(() =>
    getSavedProspectEnrollmentsPageSize(String(prospectId), 50)
  );
  const [enrollmentTabTotalPremium, setEnrollmentTabTotalPremium] = useState<
    Record<string, number>
  >({});
  const [enrollmentTabTotalLoading, setEnrollmentTabTotalLoading] = useState<
    Record<string, boolean>
  >({});

  const handlePhoneFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const formattedValue = formatPhoneInputValue(event.target.value);
    onChange(formattedValue);
  };

  const formatStateInput = (raw: string) => {
    return raw
      .replace(/[^a-zA-Z]/g, "")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatZipInput = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 9);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  const formatMoneyInput = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length === 1) return parts[0];
    return `${parts[0]}.${parts[1].slice(0, 2)}`;
  };

  const { data: prospect, isLoading: prospectLoading } = useQuery<any>({
    queryKey: [`/api/prospects/${prospectId}`],
    enabled: !!prospectId,
  });

  const { data: employees = [], isLoading: employeesLoading } = useQuery<any>({
    queryKey: [`/api/prospects/${prospectId}/employees`],
    enabled: !!prospectId,
  });

  const employeesList = Array.isArray(employees) ? employees : [];
  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(employeesList);
  }, [employeesList]);

  const { data: quotes, isLoading: quotesLoading } = useQuery<any>({
    queryKey: [`/api/prospects/${prospectId}/quotes`],
    enabled: !!prospectId,
  });

  const { data: contactInteractions, isLoading: interactionsLoading } =
    useQuery<any>({
      queryKey: [
        `/api/prospects/${prospectId}/contact-interactions?include=employees`,
      ],
      enabled: !!prospectId,
    });

  // Derive last contact date from contact history, fallback to prospect.lastContactDate
  const lastContactFromHistory: Date | null = React.useMemo(() => {
    if (!Array.isArray(contactInteractions) || contactInteractions.length === 0)
      return null;
    const validDates = contactInteractions
      .map((i: any) => parseDateLocal(i?.contactDate))
      .filter((d): d is Date => d !== null && !Number.isNaN(d.getTime()));
    if (validDates.length === 0) return null;
    const maxTs = Math.max(...validDates.map((d: Date) => d.getTime()));
    return new Date(maxTs);
  }, [contactInteractions]);

  const daysSinceLastContact: number | null = React.useMemo(() => {
    const fallback = prospect?.lastContactDate
      ? parseDateLocal(prospect.lastContactDate)
      : null;
    const refDate = lastContactFromHistory || fallback;
    if (!refDate || Number.isNaN(refDate.getTime())) return null;
    return Math.ceil((Date.now() - refDate.getTime()) / (1000 * 3600 * 24));
  }, [lastContactFromHistory, prospect?.lastContactDate]);

  // Plans and benefits data - we'll adapt client functionality for prospects
  const { data: allPlans = [], isLoading: plansLoading } = useQuery<any>({
    queryKey: ["/api/plans"],
  });

  // Prospect plan associations
  const { data: prospectPlanLinks = [], isLoading: prospectPlansLoading } =
    useQuery<any>({
      queryKey: ["/api/prospects", prospectId, "plans"],
      queryFn: () =>
        fetch(`/api/prospects/${prospectId}/plans`).then((r) => r.json()),
      enabled: !!prospectId,
    });

  // For prospects, only show plans specifically associated with this prospect
  const plansData = allPlans.filter((plan: any) =>
    prospectPlanLinks.some((cp: any) => cp.planId === plan.id)
  );

  // Helper functions for Benefits section
  const getEffectivePlan = (plan: any): any => {
    // For prospects, we just return the plan as-is since there are no customizations yet
    return plan;
  };

  // ----- Benefits > Enrollments (Prospect UI that mirrors Client EnrollmentsTab) -----
  const enrollmentEmployees = useMemo(() => {
    return (sortedEmployees || []).filter((emp: any) => emp.prospectId === prospectId);
  }, [sortedEmployees, prospectId]);

  const activePlans = useMemo(() => {
    return (plansData || []).filter((p: any) => (p?.isActive ?? true) !== false);
  }, [plansData]);

  const enrollmentsLoading = Boolean(plansLoading || prospectPlansLoading || employeesLoading);

  const planById = useMemo(() => {
    const m = new Map<number, any>();
    for (const p of activePlans as any[]) {
      if (p?.id != null) m.set(Number(p.id), p);
    }
    return m;
  }, [activePlans]);

  const prospectDerivedEnrollments = useMemo(() => {
    const out: any[] = [];
    let seq = 1;
    const emps = Array.isArray(enrollmentEmployees) ? enrollmentEmployees : [];
    const plans = Array.isArray(activePlans) ? activePlans : [];
    for (const emp of emps) {
      for (const plan of plans) {
        out.push({
          id: seq++,
          employeeId: Number(emp?.id),
          planId: Number(plan?.id),
          coverageTier: "employee_only",
          effectiveDate: plan?.effectiveDate || new Date().toISOString(),
          waivedCoverage: false,
          waiverReason: null,
          status: "active",
          premium: {
            totalMonthlyRate: 0,
            employerPremium: 0,
            employeePremium: 0,
            contributionResolved: { method: "tier_rates", key: null },
          },
          _employee: emp,
          _plan: plan,
        });
      }
    }
    return out;
  }, [enrollmentEmployees, activePlans]);

  const activeBaseEnrollments = useMemo(() => {
    const today = new Date();
    return (prospectDerivedEnrollments as any[])
      .filter((e) => (e as any).dependentId == null)
      .filter((e: any) => {
        const status = String(e?.status || "").toLowerCase();
        const isWaived = Boolean(e?.waivedCoverage);
        const ended = e?.endDate ? (parseDateLocal(e.endDate as any) ?? new Date(0)) <= today : false;
        return (
          !isWaived &&
          status !== "waived" &&
          status !== "terminated" &&
          !ended
        );
      });
  }, [prospectDerivedEnrollments]);

  const coverageTypes = useMemo(() => {
    const base = activeBaseEnrollments as any[];
    const types = Array.from(
      new Set(
        base
          .map((e) => planById.get(Number((e as any)?.planId))?.type)
          .filter(Boolean)
      )
    ) as string[];
    const ordered = [
      "health",
      "dental",
      "vision",
      "life",
      "disability",
      "voluntary",
    ].filter((t) => types.includes(t));
    return ordered.length > 0 ? ordered : types;
  }, [activeBaseEnrollments, planById]);

  React.useEffect(() => {
    if (!enrollmentCoverageTab && coverageTypes.length > 0) {
      setEnrollmentCoverageTab(coverageTypes[0]);
      return;
    }
    if (
      enrollmentCoverageTab &&
      coverageTypes.length > 0 &&
      !coverageTypes.includes(enrollmentCoverageTab)
    ) {
      setEnrollmentCoverageTab(coverageTypes[0]);
    }
  }, [enrollmentCoverageTab, coverageTypes]);

  React.useEffect(() => {
    setEnrollmentPageSize(getSavedProspectEnrollmentsPageSize(String(prospectId), 50));
  }, [prospectId]);

  const currentPage = enrollmentPageByTab[enrollmentCoverageTab] || 1;
  const setCurrentPage = (next: number) =>
    setEnrollmentPageByTab((prev) => ({
      ...prev,
      [enrollmentCoverageTab]: Math.max(1, next),
    }));

  const handlePageSizeChange = (raw: string) => {
    const n = Number.parseInt(raw, 10);
    const next = Number.isFinite(n) ? n : 50;
    setEnrollmentPageSize(next);
    saveProspectEnrollmentsPageSize(String(prospectId), next);
    setCurrentPage(1);
  };

  const enrollmentsForTab = useMemo(() => {
    if (!enrollmentCoverageTab) return [];
    return (activeBaseEnrollments as any[]).filter((e) => {
      const plan = planById.get(Number((e as any)?.planId));
      return String(plan?.type || "") === String(enrollmentCoverageTab);
    });
  }, [activeBaseEnrollments, enrollmentCoverageTab, planById]);

  const waivedEmployeesForTab = useMemo(() => {
    // Prospects don't have real enrollments/waivers wired up yet
    return [] as any[];
  }, []);

  const participationRateForTab = useMemo(() => {
    const enrolledEmployeeIds = new Set<number>();
    for (const e of enrollmentsForTab as any[]) {
      const id = Number((e as any)?.employeeId);
      if (Number.isFinite(id)) enrolledEmployeeIds.add(id);
    }
    const waivedEmployeeIds = new Set<number>();
    for (const w of waivedEmployeesForTab as any[]) {
      const id = Number((w as any)?.employeeId);
      if (Number.isFinite(id)) waivedEmployeeIds.add(id);
    }
    const total = enrolledEmployeeIds.size + waivedEmployeeIds.size;
    if (total === 0) return 0;
    return Math.round((enrolledEmployeeIds.size / total) * 100);
  }, [enrollmentsForTab, waivedEmployeesForTab]);

  const totalForTab = enrollmentsForTab.length;
  const totalPages = Math.max(1, Math.ceil(totalForTab / Math.max(1, enrollmentPageSize)));
  const clampedPage = Math.min(currentPage, totalPages);

  React.useEffect(() => {
    if (clampedPage !== currentPage) setCurrentPage(clampedPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clampedPage, currentPage, totalPages]);

  const visibleEnrollments = useMemo(() => {
    const start = (clampedPage - 1) * enrollmentPageSize;
    return enrollmentsForTab.slice(start, start + enrollmentPageSize);
  }, [clampedPage, enrollmentsForTab, enrollmentPageSize]);

  React.useEffect(() => {
    if (!enrollmentCoverageTab) return;
    let cancelled = false;

    const run = async () => {
      setEnrollmentTabTotalLoading((prev) => ({ ...prev, [enrollmentCoverageTab]: true }));
      try {
        let sum = 0;
        const base = Array.isArray(enrollmentsForTab) ? (enrollmentsForTab as any[]) : [];
        const chunkSize = 250;
        for (let i = 0; i < base.length; i += chunkSize) {
          if (cancelled) return;
          const chunk = base.slice(i, i + chunkSize);
          for (const enr of chunk) {
            const monthly = Number((enr as any)?.premium?.totalMonthlyRate || 0);
            if (Number.isFinite(monthly)) sum += monthly;
          }
          await new Promise((r) => setTimeout(r, 0));
        }
        if (cancelled) return;
        setEnrollmentTabTotalPremium((prev) => ({ ...prev, [enrollmentCoverageTab]: sum }));
      } finally {
        if (!cancelled)
          setEnrollmentTabTotalLoading((prev) => ({ ...prev, [enrollmentCoverageTab]: false }));
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [enrollmentCoverageTab, enrollmentsForTab]);

  // Debug contact interactions data
  React.useEffect(() => {
    if (contactInteractions) {
      console.log("Contact interactions loaded:", contactInteractions);
      contactInteractions.forEach((interaction: any, index: number) => {
        console.log(`Interaction ${index}:`, {
          id: interaction.id,
          subject: interaction.subject,
          nextFollowUp: interaction.nextFollowUp,
          nextFollowUpType: typeof interaction.nextFollowUp,
        });
      });
    }
  }, [contactInteractions]);

  // Filter and sort employees
  const filteredEmployees = useMemo(() => {
    let filtered = [...sortedEmployees];

    const trimmedSearch = employeeSearchTerm?.trim() ?? "";
    if (trimmedSearch.length > 0) {
      const normalizedSearch = trimmedSearch.toLowerCase();
      filtered = filtered.filter((employee: any) =>
        employee.firstName?.toLowerCase().includes(normalizedSearch) ||
        employee.lastName?.toLowerCase().includes(normalizedSearch) ||
        employee.email?.toLowerCase().includes(normalizedSearch) ||
        employee.department?.toLowerCase().includes(normalizedSearch) ||
        employee.position?.toLowerCase().includes(normalizedSearch)
      );
    }

    if (!empSortField) return filtered;

    return filtered.sort((a: any, b: any) => {
      let aValue: string;
      let bValue: string;
      switch (empSortField) {
        case 'name':
          aValue = buildNameSortKey(a, empNameSortBasis);
          bValue = buildNameSortKey(b, empNameSortBasis);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'hireDate':
          aValue = a.hireDate || '';
          bValue = b.hireDate || '';
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'position':
          aValue = (a.position || '').toLowerCase();
          bValue = (b.position || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'phone':
          aValue = (a.phone || '').toLowerCase();
          bValue = (b.phone || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (aValue < bValue) return empSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return empSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortedEmployees, employeeSearchTerm, empSortField, empSortDirection, empNameSortBasis]);

  const employeeForm = useForm({
    resolver: zodResolver(insertEmployeeSchema),
    defaultValues: {
      prospectId: prospectId,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      hireDate: "",
      department: "",
      position: "",
      employmentType: "",
      salary: "",
      status: "",
      payCycle: "",
      compensationType: "",
      departmentId: null,
      jobTitleId: null,
      locationId: null,
      employeeClassId: null,
      scheduledHours: "",
      ...emptyAddressFields(),
    },
  });

  const { defaults: employeeDefaults } = useEmployeeDefaultSettings(
    "prospect",
    String(prospectId)
  );

  const preloadEmployeeDefaults = useCallback(() => {
    const currentValues = employeeForm.getValues();
    if (employeeForm.formState.isDirty) {
      return;
    }

    const nextValues: Record<string, any> = { ...(currentValues as any) };
    let valuesChanged = false;

    const applyIfEmpty = (
      fieldName: string,
      candidate?: string
    ) => {
      if (!candidate || candidate.trim().length === 0) {
        return;
      }
      const current = nextValues[fieldName];
      if (typeof current === "string" && current.trim().length > 0) {
        return;
      }
      nextValues[fieldName] = candidate;
      valuesChanged = true;
    };

    applyIfEmpty("employmentType", employeeDefaults.employmentType);
    applyIfEmpty("status", employeeDefaults.status || "active");
    applyIfEmpty("payCycle", employeeDefaults.payCycle);
    applyIfEmpty("compensationType", employeeDefaults.compensationType);
    applyIfEmpty("city", employeeDefaults.city);
    applyIfEmpty("state", employeeDefaults.state);
    applyIfEmpty("county", employeeDefaults.county);

    const desiredCountry = employeeDefaults.country?.trim();
    if (desiredCountry && desiredCountry.length > 0) {
      const currentCountry = (nextValues.country || "").trim();
      if (
        currentCountry.length === 0 ||
        currentCountry.toLowerCase() === defaultCountry.toLowerCase()
      ) {
        nextValues.country = desiredCountry;
        valuesChanged = true;
      }
    }

    if (!valuesChanged) {
      return;
    }

    employeeForm.reset(nextValues);
  }, [employeeDefaults, employeeForm]);

  useEffect(() => {
    if (!isDialogOpen) {
      return;
    }
    preloadEmployeeDefaults();
  }, [isDialogOpen, preloadEmployeeDefaults]);

  const handleCreateProspectEmployee = (values: any) => {
    const employeePayload = {
      ...values,
      address: composeAddressLine(values),
    };

    createEmployeeMutation.mutate(employeePayload);
  };

  const editEmployeeForm = useForm<any>({
    // This dialog has no employmentType field, so omit its now-required
    // rule - otherwise every submit here would fail validation silently.
    resolver: zodResolver(insertEmployeeSchema.omit({ employmentType: true })),
    defaultValues: {
      prospectId: prospectId,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      dateOfBirth: "",
      hireDate: "",
      department: "",
      position: "",
      salary: "",
      status: "active",
      departmentId: null,
      jobTitleId: null,
      locationId: null,
      employeeClassId: null,
      ...emptyAddressFields(),
      emergencyContact: "",
      emergencyPhone: "",
    },
  });

  const createEmployeeMutation = useMutation({
    mutationFn: async (employee: any) => {
      const response = await apiRequest(
        "POST",
        `/api/prospects/${prospectId}/employees`,
        employee
      );
      const result = await response.json();
      return result;
    },
    onSuccess: () => {
      // Invalidate the exact query key used to fetch prospect employees
      queryClient.invalidateQueries({
        queryKey: [`/api/prospects/${prospectId}/employees`],
      });
      employeeForm.reset();
      setIsDialogOpen(false);
      toast({ title: "Employee added successfully" , variant: "success"});
    },
    onError: (error) => {
      console.error("Employee creation error:", error);
      toast({ title: "Failed to add employee", variant: "destructive" });
    },
  });

  const updateEmployeeMutation = useMutation({
    mutationFn: async (data: any) => {
      // Ensure all address fields are included in the update
      const addressLine = composeAddressLine(data);
      const updateData = {
        ...data,
        prospectId: parseInt(id!),
        address: addressLine || data.address || "",
        // Ensure address fields are properly included
        address1: data.address1 || "",
        address2: data.address2 || "",
        city: data.city || "",
        state: data.state || "",
        zipCode: data.zipCode || "",
        county: data.county || "",
        country: data.country || "",
        emergencyContact: data.emergencyContact || "",
        emergencyPhone: data.emergencyPhone || "",
      };
      console.log("Updating employee with data:", updateData); // Debug log
      const response = await apiRequest(
        "PATCH",
        `/api/employees/${selectedEmployee?.id}`,
        updateData
      );
      return response;
    },
    onSuccess: () => {
      // Invalidate the exact query key used to fetch prospect employees
      queryClient.invalidateQueries({
        queryKey: [`/api/prospects/${prospectId}/employees`],
      });
      editEmployeeForm.reset();
      setIsEditEmployeeDialogOpen(false);
      setSelectedEmployee(null);
      toast({ title: "Employee updated successfully" , variant: "success"});
    },
    onError: (error) => {
      console.error("Employee update error:", error);
      toast({ title: "Failed to update employee", variant: "destructive" });
    },
  });

  // Delete action is disabled in this view

  // Bulk import employees mutation
  const bulkImportEmployeesMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("csvFile", file);
      formData.append("prospectId", id!);

      const response = await fetch("/api/employees/bulk-import", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to import employees");
      }

      return response.json();
    },
    onMutate: async (file: File) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: [`/api/prospects/${prospectId}/employees`],
      });

      // Snapshot the previous value
      const previousEmployees = queryClient.getQueryData([
        `/api/prospects/${prospectId}/employees`,
      ]);

      // Optimistically update to show loading state or preview
      // We can't show actual data since we need to parse CSV, but we can show that import is in progress
      toast({
        title: "Importing employees...",
        description: "Please wait while we process your CSV file.",
      });

      // Return context with previous data for rollback
      return { previousEmployees };
    },
    onSuccess: (data) => {
      // Check if import requires approval for missing org items
      if (data.requiresApproval && (data.missingJobTitles?.length > 0 || data.missingDepartments?.length > 0 || data.missingLocations?.length > 0 || data.missingEmployeeClasses?.length > 0 || data.missingDivisions?.length > 0)) {
        setPendingOrgItems({
          jobTitles: data.missingJobTitles || [],
          departments: data.missingDepartments || [],
          locations: data.missingLocations || [],
          employeeClasses: data.missingEmployeeClasses || [],
          divisions: data.missingDivisions || [],
        });
        setIsOrgApprovalOpen(true);
        return;
      }

      // Invalidate the exact query key used to fetch prospect employees
      queryClient.invalidateQueries({
        queryKey: [`/api/prospects/${prospectId}/employees`],
      });
      const createdUsers = Number(data?.createdUsers ?? 0);
      toast({
        title: `Imported ${data.imported} employees`,
        description:
          createdUsers > 0
            ? `Welcome emails sent to ${createdUsers} new user${createdUsers === 1 ? "" : "s"}.`
            : undefined,
        variant: "success",
      });
      
      // Show skipped employees summary if any were skipped due to missing data
      if (data.skippedEmployees && data.skippedEmployees.length > 0) {
        setSkippedEmployees(data.skippedEmployees);
        setIsMissingDataDialogOpen(true);
      }
      
      if (data.errors && data.errors.length > 0) {
        console.warn("Import warnings:", data.errors);
      }
      setIsImportDialogOpen(false);
      setCsvFile(null);
      setCsvData([]);
      setImportPreview([]);
    },
    onError: (error, variables, context) => {
      // Rollback to previous state
      if (context?.previousEmployees) {
        queryClient.setQueryData(
          [`/api/prospects/${prospectId}/employees`],
          context.previousEmployees
        );
      }

      console.error("Bulk import error:", error);
      toast({
        title: "Failed to import employees",
        description: error instanceof Error ? error.message : "An error occurred during import",
        variant: "destructive"
      });
    },
    onSettled: () => {
      // Always refetch after error or success to ensure we have the latest data
      queryClient.invalidateQueries({
        queryKey: [`/api/prospects/${prospectId}/employees`],
      });
    },
  });

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === "text/csv") {
      setCsvFile(file);
      parseCsvFile(file);
    } else {
      toast({
        title: "Please select a valid CSV file",
        variant: "destructive",
      });
    }
  };

  // Helper function to parse CSV properly handling quoted fields and commas
  const parseCSVText = (csvText: string): Record<string, string>[] => {
    const lines = csvText.split(/\r?\n/).filter((line) => line.trim() !== ""); // Handle both \n and \r\n
    if (lines.length === 0) return [];

    // Parse header row
    const headerLine = lines[0];
    const headers = parseCSVLine(headerLine).map((h) => h.toLowerCase().trim());

    // Parse data rows
    const records: Record<string, string>[] = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue; // Skip empty lines

      const values = parseCSVLine(line);
      if (values.length === 0 || !values.some((v) => v.trim())) continue; // Skip empty rows

      const row: Record<string, string> = {};
      headers.forEach((header, index) => {
        row[header] = values[index] ? values[index].trim() : "";
      });
      records.push(row);
    }

    console.log("Parsed CSV records:", records); // Debug log
    return records;
  };

  // Helper function to parse a single CSV line handling quotes
  const parseCSVLine = (line: string): string[] => {
    const values: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
          // Escaped quote - add one quote and skip the next
          current += '"';
          i++;
        } else {
          // Toggle quote state
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        // End of field
        values.push(stripQuotes(current.trim()));
        current = "";
      } else {
        current += char;
      }
    }

    // Add final value
    values.push(stripQuotes(current.trim()));
    return values;
  };

  // Helper to remove surrounding quotes if present
  const stripQuotes = (value: string): string => {
    if (value.startsWith('"') && value.endsWith('"')) {
      return value.slice(1, -1);
    }
    return value;
  };

  // CSV processing functions for preview
  const parseCsvFile = (file: File) => {
    setIsProcessingCsv(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        
        // Use proper CSV parser to handle quoted fields and commas in data
        const records = parseCSVText(text);

        if (records.length === 0) {
          toast({
            title: "CSV file is empty",
            variant: "destructive",
          });
          setIsProcessingCsv(false);
          return;
        }

        setCsvText(text); // Store original CSV text
        setCsvData(records);
        setImportPreview(records.slice(0, 5)); // Show first 5 rows for preview
        setIsProcessingCsv(false);
      } catch (error) {
        console.error("CSV parsing error:", error);
        toast({
          title: "Failed to parse CSV file",
          description: (error as any).message || "Please check your CSV format",
          variant: "destructive",
        });
        setIsProcessingCsv(false);
      }
    };
    reader.readAsText(file);
  };

  const handleImportEmployees = () => {
    if (!csvFile) {
      toast({ title: "No file selected", variant: "destructive" });
      return;
    }

    bulkImportEmployeesMutation.mutate(csvFile);
  };

  // Download missing data list as CSV
  const handleDownloadMissingData = () => {
    const headers = ["Row Number", "Name", "Email", "Missing Fields"];
    const rows = skippedEmployees.map(emp => [
      emp.rowNumber,
      emp.name,
      emp.email || "N/A",
      emp.missingFields.join(", ")
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => 
        cell.toString().includes(",") ? `"${cell}"` : cell
      ).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `employees_missing_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Mutation to create missing org items during import
  const createOrgItemsMutation = useMutation({
    mutationFn: async (items: {
      jobTitles: string[];
      departments: string[];
      locations: string[];
      employeeClasses: string[];
      divisions: string[];
    }) => {
      const response = await fetch("/api/employees/import/create-org-items", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prospectId: id,
          jobTitles: items.jobTitles,
          departments: items.departments,
          locations: items.locations,
          employeeClasses: items.employeeClasses,
          divisions: items.divisions,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create organization items");
      }

      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Organization items created successfully",
        variant: "success",
      });
      setIsOrgApprovalOpen(false);
      // Retry the import with the same CSV file
      if (csvFile) {
        bulkImportEmployeesMutation.mutate(csvFile);
      }
    },
    onError: (error) => {
      console.error("Create org items error:", error);
      toast({
        title: "Failed to create organization items",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive",
      });
    },
  });

  const handleApproveOrgItems = () => {
    createOrgItemsMutation.mutate(pendingOrgItems);
  };

  const form = useForm({
    resolver: zodResolver(contactInteractionFormSchema),
    defaultValues: {
      type: "",
      subject: "",
      notes: "",
      contactDate: getCurrentLocalDateTime(),
      outcome: "",
      nextFollowUp: "",
    },
  });

  const createInteractionMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log("Form data before processing:", data);

      // Fix date handling to preserve local dates without timezone conversion
      const payload = {
        ...data,
        contactDate: parseDateLocal(data.contactDate) ?? new Date(),
        nextFollowUp: data.nextFollowUp
          ? parseDateLocal(data.nextFollowUp) ?? new Date(data.nextFollowUp + "T00:00:00")
          : undefined,
      };

      console.log("Processed payload:", payload);
      const response = await fetch(
        `/api/prospects/${prospectId}/contact-interactions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/prospects/${prospectId}/contact-interactions?include=employees`],
      });
      form.reset();
      setIsDialogOpen(false);
      toast({ title: "Contact interaction added successfully" , variant: "success"});
    },
    onError: (error) => {
      console.error("Contact interaction error:", error);
      toast({
        title: "Failed to add contact interaction",
        variant: "destructive",
      });
    },
  });

  // Helper function to convert date to input format
  const formatDateForInput = (date: any) => {
    if (!date) return "";
    if (typeof date === "string") {
      return date.split("T")[0];
    }
    return (parseDateLocal(date) ?? new Date(date)).toISOString().split("T")[0];
  };

  // Edit prospect form - temporarily remove resolver to test form submission
  const [sicOpen, setSicOpen] = useState(false);

  const editForm = useForm({
    defaultValues: {
      name: prospect?.name || "",
      primaryContactName: (prospect as any)?.primaryContactName || "none",
      contactEmail: prospect?.contactEmail || "",
      contactPhone: prospect?.contactPhone || "",
      website: (prospect as any)?.website || "",
      address: prospect?.address || "",
      ...emptyAddressFields(),
      ein: (prospect as any)?.ein || "",
      sic: (prospect as any)?.sic || "",
      sicCodeId: (prospect as any)?.sicCodeId || undefined,
      entityType: (prospect as any)?.entityType || "",
      status: prospect?.status || "lead",
      source: prospect?.source || "",
      priority: prospect?.priority || "medium",
      notes: prospect?.notes || "",
      expectedValue: prospect?.expectedValue || null,
      expectedCloseDate: formatDateForInput(prospect?.expectedCloseDate),
      lastContactDate: formatDateForInput(prospect?.lastContactDate),
    },
  });

  // Use static SIC codes array
  const sicOptions = SIC_CODES.map(sic => ({
    id: sic.code,
    code: sic.code,
    office: sic.office,
    industryTitle: sic.industryTitle,
  }));

  // Update prospect mutation
  const updateProspectMutation = useMutation({
    mutationFn: async (data: any) => {
      console.log("Updating prospect with data:", data);

      // Handle "none" value for primary contact name
      const primaryContactName =
        data.primaryContactName === "none" ? null : data.primaryContactName;

      // Ensure proper data types for the API
      const apiData = {
        ...data,
        primaryContactName,
        expectedCloseDate: data.expectedCloseDate
          ? data.expectedCloseDate
          : null,
        lastContactDate: data.lastContactDate ? data.lastContactDate : null,
      };

      console.log("Sending to API:", apiData);

      const response = await fetch(`/api/prospects/${prospectId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(apiData),
      });

      console.log("Response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Update failed:", errorText);
        throw new Error(`Failed to update prospect: ${errorText}`);
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/prospects/${prospectId}`],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      setIsEditDialogOpen(false);
      toast({ title: "Prospect updated successfully" , variant: "success"});
    },
    onError: (error) => {
      console.error("Update prospect error:", error);
      toast({ title: "Failed to update prospect", variant: "destructive" });
    },
  });

  const prospectAddressDefaults = prospect
    ? normalizeAddressFields(prospect as any)
    : emptyAddressFields();
  const prospectAddressLine = composeAddressLine(prospectAddressDefaults);
  const prospectAddressDisplayParts = [
    prospectAddressLine,
    (prospect as any)?.county,
    (prospect as any)?.country,
  ].filter(
    (value): value is string => typeof value === "string" && value.trim().length > 0
  );

  if (prospectLoading) {
    return <div className="p-8">Loading prospect details...</div>;
  }

  if (!prospect) {
    return <div className="p-8">Prospect not found</div>;
  }

  const getPriorityColor = (priority: string | null | undefined) => {
    if (!priority) return "bg-gray-100 text-gray-800";
    switch (priority.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status: string | null | undefined) => {
    if (!status) return "bg-gray-100 text-gray-800";
    switch (status.toLowerCase()) {
      case "qualified":
        return "bg-green-100 text-green-800";
      case "contacted":
        return "bg-blue-100 text-blue-800";
      case "proposal":
        return "bg-purple-100 text-purple-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <nav className="flex items-center space-x-1 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/prospects" className="hover:text-gray-700 dark:hover:text-gray-300">
          Prospects
        </Link>
        <ChevronRight className="h-4 w-4" />
        <span className="font-medium text-gray-900 dark:text-gray-100">{prospect.name}</span>
      </nav>

      {/* Header */}
      <div className="bg-white dark:bg-gray-900 shadow rounded-lg">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex-shrink-0">
                <ProfilePictureUpload
                  currentPicture={prospect.logo}
                  firstName={prospect.name}
                  lastName=""
                  uploadEndpoint={`/api/prospects/${prospect.id}/logo`}
                  deleteEndpoint={`/api/prospects/${prospect.id}/logo`}
                  fetchUrlEndpoint={`/api/prospects/${prospect.id}/logo-url`}
                  size="xl"
                  variant="logo"
                  fallbackIcon={<Building2 className="h-8 w-8" />}
                  showEditOnHover={true}
                  onPictureChange={async () => {
                    // Refetch prospect data after upload/delete
                    await queryClient.invalidateQueries({
                      queryKey: [`/api/prospects/${prospect.id}`],
                    });
                  }}
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  {prospect.name}
                </h1>
                <div className="flex items-center space-x-2 mt-1">
                  <Badge className={getStatusColor(prospect.status)}>
                    {prospect.status}
                  </Badge>
                  <Badge className={getPriorityColor(prospect.priority)}>
                    {prospect.priority}
                  </Badge>
                  <span className="text-sm text-gray-500 dark:text-gray-400"></span>
                </div>
              </div>
            </div>
            <div className="flex space-x-3">
              <PermissionButton
                permission="prospects:edit"
                whenDenied="hide"
                variant="outline"
                onClick={() => {
                  const addressDefaults = normalizeAddressFields(prospect as any);
                  editForm.reset({
                    name: prospect?.name || "",
                    primaryContactName: (prospect as any)?.primaryContactName || "none",
                    contactEmail: prospect?.contactEmail || "",
                    contactPhone: prospect?.contactPhone || "",
                    website: (prospect as any)?.website || "",
                    address: prospect?.address || "",
                    ...addressDefaults,
                    ein: (prospect as any)?.ein || "",
                    sic: (prospect as any)?.sic || "",
                    sicCodeId: (prospect as any)?.sicCodeId || undefined,
                    entityType: (prospect as any)?.entityType || "",
                    status: prospect?.status || "lead",
                    source: prospect?.source || "",
                    priority: prospect?.priority || "medium",
                    notes: prospect?.notes || "",
                    expectedValue: prospect?.expectedValue || "",
                    expectedCloseDate: formatDateForInput(
                      prospect?.expectedCloseDate
                    ),
                    lastContactDate: formatDateForInput(
                      prospect?.lastContactDate
                    ),
                  });
                  setIsEditDialogOpen(true);
                }}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Prospect
              </PermissionButton>
              {prospect?.clientId ? (
                <Link href={`/clients/${prospect.clientId}`}>
                  <Button variant="default">
                    <ChevronRight className="h-4 w-4 mr-2" />
                    View Client
                  </Button>
                </Link>
              ) : (
                <PermissionButton
                  permission="prospects:edit"
                  whenDenied="hide"
                  variant="default"
                  onClick={() => setIsConvertConfirmOpen(true)}
                >
                  <ChevronRight className="h-4 w-4 mr-2" />
                  Convert to Client
                </PermissionButton>
              )}
              <Button
                onClick={() => {
                  if (!prospect) return;
                  calendarService.scheduleMeeting({
                    type: "prospect",
                    id: prospect.id,
                    name: prospect.name,
                    email: prospect.contactEmail || undefined,
                    phone: prospect.contactPhone || undefined,
                  });
                }}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Meeting
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="employees">Employees</TabsTrigger>
          <TabsTrigger value="benefits">Benefits</TabsTrigger>
          <TabsTrigger value="quotes">Quotes & Proposals</TabsTrigger>
          <TabsTrigger value="contact-history">Contact History</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="organization">Organization</TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="space-y-6">
          <ProspectOrganizationTab prospectId={parseInt(id!, 10)} />
        </TabsContent>
        <TabsContent value="profile" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid gap-6 md:grid-cols-4">
            <Card>
              <CardContent className="flex items-center p-6">
                <DollarSign className="h-8 w-8 text-green-600 mr-4" />
                <div>
                  <p className="text-2xl font-bold">
                    $
                    {prospect.expectedValue
                      ? parseFloat(prospect.expectedValue).toLocaleString()
                      : "0"}
                  </p>
                  <p className="text-sm text-gray-500">Expected Value</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center p-6">
                <Calendar className="h-8 w-8 text-purple-600 mr-4" />
                <div>
                  <p className="text-2xl font-bold">
                    {typeof daysSinceLastContact === "number"
                      ? daysSinceLastContact
                      : "N/A"}
                  </p>
                  <p className="text-sm text-gray-500">Days Since Contact</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center p-6">
                <History className="h-8 w-8 text-orange-600 mr-4" />
                <div>
                  <p className="text-2xl font-bold">
                    {Array.isArray(contactInteractions)
                      ? contactInteractions.length
                      : 0}
                  </p>
                  <p className="text-sm text-gray-500">Contact Interactions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="flex items-center p-6">
                <User className="h-8 w-8 text-blue-600 mr-4" />
                <div>
                  <p className="text-2xl font-bold">{employees?.length || 0}</p>
                  <p className="text-sm text-gray-500">Employees Added</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Mail className="h-5 w-5 mr-2" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span>{prospect.contactEmail}</span>
                </div>
                {prospect.contactPhone && (
                  <div className="flex items-center space-x-3">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span>{prospect.contactPhone}</span>
                  </div>
                )}
                {prospectAddressDisplayParts.length > 0 && (
                  <div className="flex items-center space-x-3">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <div className="flex flex-col">
                      {prospectAddressDisplayParts.map((line) => (
                        <span key={line}>{line}</span>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Company Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2" />
                  Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Status:</span>
                  <Badge
                    variant={
                      prospect.status === "active" ? "default" : "secondary"
                    }
                  >
                    {prospect.status}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Priority:</span>
                  <Badge
                    variant={
                      prospect.priority === "high"
                        ? "destructive"
                        : prospect.priority === "medium"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {prospect.priority}
                  </Badge>
                </div>
                <div className="flex justify-between"></div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Expected Value:</span>
                  <span className="font-medium dark:text-gray-100">
                    {prospect.expectedValue
                      ? `$${parseFloat(
                          prospect.expectedValue
                        ).toLocaleString()}`
                      : "Not specified"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Source:</span>
                  <span className="font-medium dark:text-gray-100">
                    {formatSource(prospect.source)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Expected Close:</span>
                  <span className="font-medium dark:text-gray-100">
                    {prospect.expectedCloseDate
                      ? formatDate(prospect.expectedCloseDate)
                      : "Not set"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Created:</span>
                  <span className="font-medium dark:text-gray-100">
                    {prospect.createdAt
                      ? formatDate(prospect.createdAt)
                      : "Unknown"}
                  </span>
                </div>
          </CardContent>
        </Card>
      </div>

      {/* Agency Representatives & Defaults */}
      <div className="grid gap-6 md:grid-cols-2">
        <AgencyRepresentatives
          entityType="prospect"
          entityId={prospectId}
          currentTeamId={prospect?.teamId}
        />
        <EmployeeDefaultSettingsCard
          ownerType="prospect"
          ownerId={String(prospectId)}
          heading="Employee Defaults"
          description="Use these values as the baseline when adding prospect employees."
        />
      </div>

        </TabsContent>

        <TabsContent value="employees" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Employees</h3>
            <div className="flex items-center space-x-2">
              <Select
                value={empNameSortBasis}
                onValueChange={(v) => {
                  // also activates the sort, not just the basis for the next header click
                  setEmpNameSortBasis(v as 'firstName' | 'lastName');
                  setEmpSortField('name');
                  setEmpSortDirection('asc');
                }}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Sort name by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="firstName">First Name</SelectItem>
                  <SelectItem value="lastName">Last Name</SelectItem>
                </SelectContent>
              </Select>
              <div className="relative">
                <Input
                  placeholder="Search employees..."
                  value={employeeSearchTerm}
                  onChange={(e) => setEmployeeSearchTerm(e.target.value)}
                  className="w-64"
                />
              </div>
              <Button
                variant="outline"
                onClick={async () => {
                  // Fetch dependents for all employees, plus full SSNs (list rows are masked)
                  const employeesWithDependents = await Promise.all(
                    sortedEmployees.map(async (emp: any) => {
                      let dependents: any[] = [];
                      try {
                        const res = await fetch(
                          `/api/employees/${emp.id}/dependents`
                        );
                        const body = res.ok ? await res.json() : [];
                        dependents = Array.isArray(body) ? body : [];
                      } catch {
                        // export the employee without dependents
                      }
                      try {
                        const merged = applyFullSsns(emp, dependents, await fetchFullSsns(emp.id, "export"));
                        return { ...merged.employee, dependents: merged.dependents };
                      } catch {
                        return { ...emp, dependents };
                      }
                    })
                  );

                  // Prepare headers
                  const headers = [
                    "id",
                    "roleId",
                    "clientId",
                    "prospectId",
                    "firstName",
                    "lastName",
                    "email",
                    "phone",
                    "dateOfBirth",
                    "sex",
                    "maritalStatus",
                    "ssn",
                    "tobaccoUser",
                    "disabled",
                    "hireDate",
                    "department",
                    "position",
                    "employmentType",
                    "employeeId",
                    "scheduledHours",
                    "compensationType",
                    "compensationAmount",
                    "payCycle",
                    "salary",
                    "status",
                    "address1",
                    "address2",
                    "city",
                    "state",
                    "zipCode",
                    "county",
                    "country",
                    "emergencyContact",
                    "emergencyPhone",
                    "profilePicture",
                    "createdAt",
                    "locationId",
                    "employeeClassId",
                    "divisionId",
                  ];

                  // Add dependent columns (up to 5 dependents)
                  for (let i = 1; i <= 5; i++) {
                    headers.push(
                      `dependent${i}_firstName`,
                      `dependent${i}_lastName`,
                      `dependent${i}_relationship`,
                      `dependent${i}_dateOfBirth`,
                      `dependent${i}_ssn`,
                      `dependent${i}_sex`
                    );
                  }

                  // Prepare data mapper
                  const dataMapper = (emp: any) => {
                    const row = [
                      emp.id,
                      emp.roleId || "",
                      emp.clientId || "",
                      emp.prospectId || "",
                      emp.firstName,
                      emp.lastName,
                      emp.email || "",
                      emp.phone || "",
                      emp.dateOfBirth || "",
                      emp.sex || "",
                      emp.maritalStatus || "",
                      emp.ssn || "",
                      emp.tobaccoUser || "",
                      emp.disabled || "",
                      emp.hireDate || "",
                      emp.department || "",
                      emp.position || "",
                      emp.employmentType || "",
                      emp.employeeId || "",
                      emp.scheduledHours || "",
                      emp.compensationType || "",
                      emp.compensationAmount || "",
                      emp.payCycle || "",
                      emp.salary || "",
                      emp.status || "",
                      emp.address1 || "",
                      emp.address2 || "",
                      emp.city || "",
                      emp.state || "",
                      emp.zipCode || "",
                      emp.county || "",
                      emp.country || "",
                      emp.emergencyContact || "",
                      emp.emergencyPhone || "",
                      emp.profilePicture || "",
                      emp.createdAt || "",
                      emp.locationId || "",
                      emp.employeeClassId || "",
                      emp.divisionId || "",
                    ];

                    // Add dependent data
                    const dependents = emp.dependents || [];
                    for (let i = 1; i <= 5; i++) {
                      const dep = dependents[i - 1];
                      row.push(
                        dep?.firstName || "",
                        dep?.lastName || "",
                        dep?.relationship || "",
                        dep?.dateOfBirth || "",
                        dep?.ssn || "",
                        dep?.sex || ""
                      );
                    }

                    return row;
                  };

                  exportToCSV(employeesWithDependents, {
                    filename: `${prospect?.name || "prospect"}_employees.csv`,
                    headers,
                    dataMapper,
                  });
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <PermissionButton
                permission="prospects:edit"
                whenDenied="hide"
                variant="outline"
                onClick={() => setIsImportDialogOpen(true)}
              >
                <FileUp className="h-4 w-4 mr-2" />
                Import
              </PermissionButton>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <PermissionButton
                    permission="prospects:edit"
                    whenDenied="hide"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Add Employee
                  </PermissionButton>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Employee</DialogTitle>
                  </DialogHeader>
                  <Form {...employeeForm}>
                    <form
                      onSubmit={employeeForm.handleSubmit(
                        handleCreateProspectEmployee
                      )}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First Name</FormLabel>
                              <FormControl>
                                <Input placeholder="John" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john.doe@company.com"
                                  {...field}
                                  onBlur={(e) =>
                                    field.onChange(
                                      e.target.value.trim().toLowerCase()
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="phone"
                          render={({ field }) => {
                            const handlePhoneChange = (
                              event: ChangeEvent<HTMLInputElement>
                            ) => {
                              handlePhoneFieldChange(event, field.onChange);
                            };

                            return (
                              <FormItem>
                                <FormLabel>Phone</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    value={field.value || ""}
                                    placeholder="(555) 123-4567"
                                    onChange={handlePhoneChange}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="departmentId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Department</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="department"
                                  ownerType="prospect"
                                  ownerId={prospectId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Department"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="jobTitleId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Job Title</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="job-title"
                                  ownerType="prospect"
                                  ownerId={prospectId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Job Title"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="locationId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Location</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="location"
                                  ownerType="prospect"
                                  ownerId={prospectId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Location"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="employeeClassId"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Employee Class</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="employee-class"
                                  ownerType="prospect"
                                  ownerId={prospectId}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Employee Class"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="compensationType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Compensation Type</FormLabel>
                              <Select
                                value={field.value || ""}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select compensation type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {COMPENSATION_TYPE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="payCycle"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pay Cycle</FormLabel>
                              <Select
                                value={field.value || ""}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select pay cycle" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PAY_CYCLE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="employmentType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Employment Type</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select employment type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {EMPLOYMENT_TYPES.map((t) => (
                                    <SelectItem key={t} value={t}>
                                      {t}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="scheduledHours"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Hours Worked (Weekly)</FormLabel>
                              <FormControl>
                                <Input placeholder="40" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="hireDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Hire Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="dateOfBirth"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Date of birth</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Address Fields */}
                      <AddressFields control={employeeForm.control} />

                      <div className="flex justify-end space-x-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={createEmployeeMutation.isPending}
                        >
                          {createEmployeeMutation.isPending
                            ? "Adding..."
                            : "Add Employee"}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          {employeesLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredEmployees.length > 0 ? (
            <div className="bg-white dark:bg-gray-900 shadow overflow-hidden sm:rounded-md">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('name')}>
                        <div className="flex items-center">Employee Name{getEmpSortIcon('name')}</div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('status')}>
                        <div className="flex items-center">Status{getEmpSortIcon('status')}</div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('hireDate')}>
                        <div className="flex items-center">Hire Date{getEmpSortIcon('hireDate')}</div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('department')}>
                        <div className="flex items-center">Department{getEmpSortIcon('department')}</div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('position')}>
                        <div className="flex items-center">Title{getEmpSortIcon('position')}</div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('email')}>
                        <div className="flex items-center">Email{getEmpSortIcon('email')}</div>
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none" onClick={() => handleEmployeeSort('phone')}>
                        <div className="flex items-center">Phone{getEmpSortIcon('phone')}</div>
                      </th>
                      <th scope="col" className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredEmployees.map((employee: any) => (
                      <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <User className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <Link href={`/employee/${employee.id}`}>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer">
                                  {employee.firstName} {employee.lastName}
                                </div>
                              </Link>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 text-xs font-semibold rounded-full ${
                              employee.status === "active"
                                ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                : "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
                            }`}
                          >
                            {employee.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {formatDateDisplay(employee.hireDate)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.department || "Not specified"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.position || "Not specified"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {employee.phone || "N/A"}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <PermissionButton
                              permission="prospects:edit"
                              whenDenied="hide"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedEmployee(employee);
                                const addressDefaults = normalizeAddressFields(
                                  employee as any
                                );
                                editEmployeeForm.reset({
                                  prospectId: prospectId,
                                  firstName: employee.firstName || "",
                                  lastName: employee.lastName || "",
                                  email: employee.email || "",
                                  phone: employee.phone || "",
                                  dateOfBirth: employee.dateOfBirth
                                    ? (parseDateLocal(employee.dateOfBirth) ?? new Date(employee.dateOfBirth))
                                        .toISOString()
                                        .split("T")[0]
                                    : "",
                                  hireDate: employee.hireDate
                                    ? (parseDateLocal(employee.hireDate) ?? new Date(employee.hireDate))
                                        .toISOString()
                                        .split("T")[0]
                                    : "",
                                  department: employee.department || "",
                                  position: employee.position || "",
                                  salary: employee.salary || "",
                                  status: employee.status || "active",
                                  departmentId:
                                    (employee as any)?.departmentId ?? null,
                                  jobTitleId: (employee as any)?.jobTitleId ?? null,
                                  locationId: (employee as any)?.locationId ?? null,
                                  employeeClassId:
                                    (employee as any)?.employeeClassId ?? null,
                                  ...addressDefaults,
                                  emergencyContact:
                                    employee.emergencyContact || "",
                                  emergencyPhone: employee.emergencyPhone || "",
                                });
                                setIsEditEmployeeDialogOpen(true);
                              }}
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </PermissionButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow sm:rounded-md p-6 text-center">
              <User className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                {employees.length === 0
                  ? "No employees"
                  : "No employees match your search"}
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                {employees.length === 0
                  ? "Get started by adding your first employee."
                  : "Try adjusting your search terms."}
              </p>
              <div className="mt-6">
                <PermissionButton
                  permission="prospects:edit"
                  whenDenied="hide"
                  onClick={() => setIsDialogOpen(true)}
                >
                  <User className="h-4 w-4 mr-2" />
                  Add Employee
                </PermissionButton>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="benefits" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Benefits & Plans</h3>
            <PermissionButton
              permission="prospects:edit"
              whenDenied="hide"
              onClick={() => setIsAddPlanDialogOpen(true)}
            >
              <Shield className="h-4 w-4 mr-2" />
              Add Plan
            </PermissionButton>
          </div>

          {/* Benefits Sub-tabs */}
          <Tabs defaultValue="overview" className="space-y-4">
            <div className="flex items-center justify-between">
              <TabsList className="h-9 bg-muted">
                <TabsTrigger value="overview" className="text-xs h-7">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="enrollments" className="text-xs h-7">
                  Enrollments
                </TabsTrigger>
                <TabsTrigger value="open-enrollment" className="text-xs h-7">
                  Open Enrollment
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-4">
              <div className="space-y-6">
                {/* Overview Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="h-5 w-5" />
                      Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border">
                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse">
                          <thead>
                            <tr className="border-b bg-muted/50">
                              <th className="px-4 py-3 text-left text-sm font-medium">
                                Carrier
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium">
                                Coverage Type
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium">
                                Group Number
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium">
                                Effective Date
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium">
                                Enrolled
                              </th>
                              <th className="px-4 py-3 text-left text-sm font-medium">
                                Total Premium
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {plansData?.length ? (
                              plansData.map((plan: any) => {
                                const effectivePlan = getEffectivePlan(plan);
                                return (
                                  <tr
                                    key={plan.id}
                                    className="border-b hover:bg-muted/25"
                                  >
                                    <td className="px-4 py-3 text-sm">
                                      {effectivePlan.carrier}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      <Badge
                                        variant="outline"
                                        className="capitalize"
                                      >
                                        {effectivePlan.type}
                                      </Badge>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-mono">
                                      {effectivePlan.planCode || "N/A"}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {formatDate(
                                        effectivePlan.effectiveDate
                                      ) !== "TBD"
                                        ? formatDate(
                                            effectivePlan.effectiveDate
                                          )
                                        : formatDate(plan.createdAt)}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      <div className="flex items-center gap-1">
                                        <Users className="h-3 w-3" />
                                        {
                                          employeesList.filter(
                                            (emp: any) =>
                                              emp.prospectId === parseInt(id!)
                                          ).length
                                        }{" "}
                                        employees
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-sm font-medium">
                                      $
                                      {(
                                        employeesList.filter(
                                          (emp: any) =>
                                            emp.prospectId === parseInt(id!)
                                        ).length * 450
                                      ).toLocaleString()}
                                    </td>
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td
                                  colSpan={6}
                                  className="px-4 py-8 text-center text-muted-foreground"
                                >
                                  No active plans configured
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Enrollment Status Section */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Enrollment Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="border rounded-lg p-4">
                          <h4 className="font-medium text-sm mb-2">
                            Newly Eligible
                          </h4>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                              <Users className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold">
                                {
                                  employeesList.filter((emp: any) => {
                                    if (!emp.hireDate) return false;
                                    const hireDate = parseDateLocal(emp.hireDate);
                                    if (!hireDate) return false;
                                    const eligibleDate = new Date(hireDate);
                                    eligibleDate.setDate(
                                      eligibleDate.getDate() + 60
                                    ); // 60-day waiting period
                                    const now = new Date();
                                    return (
                                      eligibleDate <= now &&
                                      eligibleDate >=
                                        new Date(
                                          now.getTime() -
                                            30 * 24 * 60 * 60 * 1000
                                        )
                                    );
                                  }).length
                                }
                              </p>
                              <p className="text-xs text-muted-foreground">
                                This month
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="border rounded-lg p-4">
                          <h4 className="font-medium text-sm mb-2">
                            Pending Changes
                          </h4>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                              <Clock className="h-4 w-4 text-yellow-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold">0</p>
                              <p className="text-xs text-muted-foreground">
                                Changes pending
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="border rounded-lg p-4">
                          <h4 className="font-medium text-sm mb-2">
                            Terminated
                          </h4>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                              <UserX className="h-4 w-4 text-red-600" />
                            </div>
                            <div>
                              <p className="text-2xl font-bold">
                                {
                                  employeesList.filter(
                                    (emp: any) => emp.status === "terminated"
                                  ).length
                                }
                              </p>
                              <p className="text-xs text-muted-foreground">
                                This month
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Benefits Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="flex items-center p-6">
                      <Shield className="h-8 w-8 text-green-600 mr-4" />
                      <div>
                        <p className="text-2xl font-bold">0</p>
                        <p className="text-sm text-gray-500">Active Plans</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex items-center p-6">
                      <DollarSign className="h-8 w-8 text-yellow-600 mr-4" />
                      <div>
                        <p className="text-2xl font-bold">$0</p>
                        <p className="text-sm text-gray-500">Monthly Premium</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="flex items-center p-6">
                      <FileText className="h-8 w-8 text-purple-600 mr-4" />
                      <div>
                        <p className="text-2xl font-bold">0</p>
                        <p className="text-sm text-gray-500">Documents</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Available Plans Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Available Plans for Proposal
                    </CardTitle>
                    <Button onClick={() => setIsAddPlanDialogOpen(true)}>
                      <Shield className="h-4 w-4 mr-2" />
                      Add Plan
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="px-4 py-3 text-left text-sm font-medium">
                              Plan Name
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium">
                              Carrier
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium">
                              Coverage Type
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium">
                              Policy Number
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-medium">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {plansData?.length ? (
                            plansData.map((plan: any) => {
                              const effectivePlan = getEffectivePlan(plan);
                              return (
                                <tr
                                  key={plan.id}
                                  className="border-b hover:bg-muted/25"
                                >
                                  <td className="px-4 py-3">
                                    <div>
                                      <p className="font-medium text-sm">
                                        {effectivePlan.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {effectivePlan.description ||
                                          "No description"}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    {effectivePlan.carrier}
                                  </td>
                                  <td className="px-4 py-3 text-sm">
                                    <Badge
                                      variant="outline"
                                      className="capitalize"
                                    >
                                      {effectivePlan.type}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-3 text-sm font-mono">
                                    {effectivePlan.planCode || "TBD"}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex gap-2">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() =>
                                          setLocation(
                                            `/plans/${plan.id}?prospectId=${id}`
                                          )
                                        }
                                      >
                                        View Details
                                      </Button>
                                      <Button size="sm" variant="secondary">
                                        Add to Proposal
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td
                                colSpan={5}
                                className="px-4 py-8 text-center text-muted-foreground"
                              >
                                <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                <p>No plans available for proposal</p>
                                <Button
                                  className="mt-2"
                                  size="sm"
                                  onClick={() => setIsAddPlanDialogOpen(true)}
                                >
                                  Add First Plan
                                </Button>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {/* Add Plan Dialog */}
              <Dialog
                open={isAddPlanDialogOpen}
                onOpenChange={setIsAddPlanDialogOpen}
              >
                <DialogContent className="max-w-xl">
                  <DialogHeader>
                    <DialogTitle>Add Plan to Prospect</DialogTitle>
                    <DialogDescription>
                      Select a plan and optional rating area to add to this
                      prospect.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Plan</Label>
                      <Select
                        value={selectedPlanId}
                        onValueChange={setSelectedPlanId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a plan" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.isArray(allPlans) &&
                            allPlans
                              .filter((p: any) => p.isActive)
                              .map((p: any) => (
                                <SelectItem key={p.id} value={String(p.id)}>
                                  {p.name} · {p.carrier}
                                </SelectItem>
                              ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Rating Area (optional)</Label>
                      <Select
                        value={selectedAreaId}
                        onValueChange={setSelectedAreaId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select rating area" />
                        </SelectTrigger>
                        <SelectContent>
                          {[...Array(19)].map((_, i) => {
                            const id = i + 1;
                            return (
                              <SelectItem key={id} value={String(id)}>
                                AREA {id}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsAddPlanDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={async () => {
                          try {
                            if (!selectedPlanId) return;
                            const res = await fetch(
                              `/api/prospects/${prospectId}/plans`,
                              {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({
                                  planId: Number(selectedPlanId),
                                  ratingAreaId: selectedAreaId
                                    ? Number(selectedAreaId)
                                    : null,
                                  ratingAreaLabel: selectedAreaId
                                    ? `AREA ${selectedAreaId}`
                                    : null,
                                }),
                              }
                            );
                            if (!res.ok)
                              throw new Error(`Failed: ${res.status}`);
                            await res.json();
                            setIsAddPlanDialogOpen(false);
                            setSelectedPlanId("");
                            setSelectedAreaId("");
                            queryClient.invalidateQueries({
                              queryKey: ["/api/prospects", prospectId, "plans"],
                            });
                            toast({ title: "Plan added to prospect" , variant: "success"});
                          } catch (e: any) {
                            toast({
                              title: "Failed to add plan",
                              description: e.message,
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="enrollments" className="space-y-4">
              <div className="space-y-4 pb-28">
                <div className="flex items-center justify-between">
                  <h4 className="text-md font-medium">Employee Enrollments</h4>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const rows = (activeBaseEnrollments as any[]).map((enr: any) => {
                          const emp = enrollmentEmployees.find((e: any) => e.id === enr.employeeId);
                          const plan = planById.get(Number(enr.planId));
                          return {
                            Employee: emp ? `${emp.firstName} ${emp.lastName}` : `#${enr.employeeId}`,
                            Dependents: "",
                            Plan: plan?.name || "",
                            "Coverage Type": String(enr.coverageTier || "").replace(/_/g, " "),
                            Carrier: plan?.carrier || "",
                            Rate: Number(enr?.premium?.totalMonthlyRate || 0),
                            "Employer Contribution": Number(enr?.premium?.employerPremium || 0),
                            "Employee Contribution": Number(enr?.premium?.employeePremium || 0),
                          };
                        });
                        exportToCSV(rows as any[], {
                          filename: `prospect_enrollments_${new Date().toISOString().slice(0, 10)}`,
                          toast: { success: "Enrollments exported" },
                        });
                      }}
                    >
                      Export CSV
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const rows = (activeBaseEnrollments as any[]).map((enr: any) => {
                          const emp = enrollmentEmployees.find((e: any) => e.id === enr.employeeId);
                          const plan = planById.get(Number(enr.planId));
                          return {
                            Employee: emp ? `${emp.firstName} ${emp.lastName}` : `#${enr.employeeId}`,
                            Dependents: "",
                            Plan: plan?.name || "",
                            "Coverage Type": String(enr.coverageTier || "").replace(/_/g, " "),
                            Carrier: plan?.carrier || "",
                            Rate: Number(enr?.premium?.totalMonthlyRate || 0),
                            "Employer Contribution": Number(enr?.premium?.employerPremium || 0),
                            "Employee Contribution": Number(enr?.premium?.employeePremium || 0),
                          };
                        });

                        const w = window.open("", "_blank");
                        if (!w) return;
                        const style = `
                          <style>
                            body { font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; padding: 16px; }
                            h1 { font-size: 18px; margin-bottom: 12px; }
                            table { width: 100%; border-collapse: collapse; font-size: 12px; }
                            th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
                            th { background: #f3f4f6; }
                          </style>`;
                        const headers = [
                          "Employee",
                          "Dependents",
                          "Plan",
                          "Coverage Type",
                          "Carrier",
                          "Rate",
                          "Employer Contribution",
                          "Employee Contribution",
                        ];
                        const rowsHtml = rows
                          .map((r: any) => {
                            const cells = headers
                              .map((h) => {
                                const v = r[h];
                                const isMoney =
                                  h === "Rate" ||
                                  h === "Employer Contribution" ||
                                  h === "Employee Contribution";
                                return `<td>${isMoney ? formatCurrency(v) : String(v ?? "")}</td>`;
                              })
                              .join("");
                            return `<tr>${cells}</tr>`;
                          })
                          .join("");
                        const html = `<!doctype html><html><head><meta charset="utf-8"/>${style}</head><body>
                          <h1>Employee Enrollments</h1>
                          <table><thead><tr>${headers
                            .map((h) => `<th>${h}</th>`)
                            .join("")}</tr></thead><tbody>${rowsHtml}</tbody></table>
                        </body></html>`;
                        w.document.open();
                        w.document.write(html);
                        w.document.close();
                        w.focus();
                        w.print();
                      }}
                    >
                      Export PDF
                    </Button>
                    <Dialog open={isEnrollmentDialogOpen} onOpenChange={setIsEnrollmentDialogOpen}>
                      <DialogTrigger asChild>
                        <Button>
                          <Plus className="h-4 w-4 mr-2" />
                          Add Enrollment
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>New Enrollment</DialogTitle>
                        </DialogHeader>
                        <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                          Enrollments for prospects aren’t wired up yet. This UI mirrors the client experience for layout consistency.
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {enrollmentsLoading ? (
                  <Card>
                    <CardContent className="py-6">Loading enrollments…</CardContent>
                  </Card>
                ) : prospectDerivedEnrollments.length > 0 ? (
                  <div className="space-y-4">
                    {(() => {
                      if (!coverageTypes || coverageTypes.length === 0) return null;
                      const label = coverageTypeLabel;
                      const start =
                        totalForTab === 0 ? 0 : (clampedPage - 1) * enrollmentPageSize + 1;
                      const end = Math.min(totalForTab, clampedPage * enrollmentPageSize);
                      return (
                        <Tabs
                          value={enrollmentCoverageTab}
                          onValueChange={(v) => {
                            setEnrollmentCoverageTab(v);
                            setEnrollmentPageByTab((prev) => ({ ...prev, [v]: 1 }));
                          }}
                        >
                          <TabsList className="mb-2">
                            {coverageTypes.map((t) => (
                              <TabsTrigger key={t} value={t} className="text-xs h-7">
                                {label(t)} Coverage
                              </TabsTrigger>
                            ))}
                          </TabsList>

                          {coverageTypes.map((t) => (
                            <TabsContent key={t} value={t} className="mt-0">
                              <Card>
                                <CardHeader>
                                  <div className="flex items-start justify-between gap-4">
                                    <CardTitle className="flex items-center gap-2">
                                      <Shield className="h-5 w-5" />
                                      {label(t)} Coverage
                                    </CardTitle>
                                    <div className="text-right">
                                      <div className="text-sm font-semibold text-muted-foreground">
                                        Total Premium
                                      </div>
                                      <div className="text-xl font-semibold">
                                        {enrollmentTabTotalLoading[t]
                                          ? "…"
                                          : formatCurrency(enrollmentTabTotalPremium[t] || 0)}
                                      </div>
                                    </div>
                                  </div>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                  <div className="rounded-md border">
                                    <div className="overflow-x-auto">
                                      <table className="w-full border-collapse">
                                        <thead>
                                          <tr className="border-b bg-muted/50">
                                            <th className="px-4 py-3 text-left text-sm font-medium">
                                              Employee
                                            </th>
                                            <th className="px-4 py-3 text-left text-sm font-medium">
                                              Plan
                                            </th>
                                            <th className="px-4 py-3 text-left text-sm font-medium w-[220px]">
                                              Coverage Tier
                                            </th>
                                            <th className="px-4 py-3 text-right text-sm font-medium">
                                              Total Premium
                                            </th>
                                            <th className="px-4 py-3 text-right text-sm font-medium">
                                              Employer Contrib.
                                            </th>
                                            <th className="px-4 py-3 text-right text-sm font-medium">
                                              Employee Contrib.
                                            </th>
                                            <th className="px-4 py-3 text-right text-sm font-medium">
                                              Actions
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(t === enrollmentCoverageTab ? visibleEnrollments : []).map(
                                            (enr: any) => {
                                              const emp = enrollmentEmployees.find(
                                                (e: any) => e.id === (enr as any).employeeId
                                              );
                                              const plan = planById.get(Number((enr as any).planId));
                                              const isOpen = !!enrollmentExpandedRows[(enr as any).id];
                                              const result = (enr as any)?.premium;

                                              return (
                                                <React.Fragment key={(enr as any).id}>
                                                  <tr
                                                    className="border-b hover:bg-muted/25 cursor-pointer"
                                                    onClick={() =>
                                                      setEnrollmentExpandedRows((prev) => ({
                                                        ...prev,
                                                        [(enr as any).id]: !prev[(enr as any).id],
                                                      }))
                                                    }
                                                  >
                                                    <td className="px-4 py-3 text-sm">
                                                      <div className="flex items-center gap-3">
                                                        <div className="flex items-center gap-2">
                                                          {isOpen ? (
                                                            <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                          ) : (
                                                            <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                                          )}
                                                          <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                                                            <User className="h-4 w-4 text-muted-foreground" />
                                                          </div>
                                                        </div>
                                                        <div className="min-w-0">
                                                          <div className="flex items-center gap-2">
                                                            {emp ? (
                                                              <Link
                                                                href={`/employee/${emp.id}`}
                                                                className="font-medium hover:underline truncate"
                                                                onClick={(ev) => ev.stopPropagation()}
                                                              >
                                                                {emp.firstName} {emp.lastName}
                                                              </Link>
                                                            ) : (
                                                              <span className="font-medium">
                                                                #{(enr as any).employeeId}
                                                              </span>
                                                            )}
                                                          </div>
                                                          {emp?.department && (
                                                            <div className="text-xs text-muted-foreground truncate">
                                                              {emp.department}
                                                            </div>
                                                          )}
                                                        </div>
                                                      </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                      <div className="flex flex-col gap-1">
                                                        <span className="font-medium">
                                                          {plan?.name || `Plan #${(enr as any).planId}`}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                          {plan?.carrier || ""}
                                                        </span>
                                                      </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm">
                                                      <Badge
                                                        variant="outline"
                                                        className="capitalize bg-background border-border text-foreground shadow-sm"
                                                      >
                                                        {String((enr as any)?.coverageTier || "").replace(
                                                          /_/g,
                                                          " "
                                                        ) || "—"}
                                                      </Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm">
                                                      <div className="flex flex-col items-end">
                                                        <span className="font-medium">
                                                          {formatCurrency(result?.totalMonthlyRate || 0)}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                          per month
                                                        </span>
                                                      </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm">
                                                      <div className="flex flex-col items-end">
                                                        <span className="font-medium">
                                                          {formatCurrency(result?.employerPremium || 0)}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                          per month
                                                        </span>
                                                      </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm">
                                                      <div className="flex flex-col items-end">
                                                        <span className="font-medium">
                                                          {formatCurrency(result?.employeePremium || 0)}
                                                        </span>
                                                        <span className="text-xs text-muted-foreground">
                                                          per pay period
                                                        </span>
                                                      </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-right text-sm">
                                                      <div
                                                        className="flex items-center justify-end gap-2"
                                                        onClick={(ev) => ev.stopPropagation()}
                                                      >
                                                        <Button variant="outline" size="sm" disabled>
                                                          Edit
                                                        </Button>
                                                        <Button variant="outline" size="sm" disabled>
                                                          View
                                                        </Button>
                                                        <AlertDialog>
                                                          <AlertDialogTrigger asChild>
                                                            <Button
                                                              variant="outline"
                                                              size="sm"
                                                              className="px-2"
                                                              aria-label="Delete enrollment"
                                                              disabled
                                                            >
                                                              <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                          </AlertDialogTrigger>
                                                          <AlertDialogContent>
                                                            <AlertDialogHeader>
                                                              <AlertDialogTitle>
                                                                Delete enrollment?
                                                              </AlertDialogTitle>
                                                              <AlertDialogDescription>
                                                                This action is not available for prospects yet.
                                                              </AlertDialogDescription>
                                                            </AlertDialogHeader>
                                                            <AlertDialogFooter>
                                                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                              <AlertDialogAction disabled>
                                                                Delete
                                                              </AlertDialogAction>
                                                            </AlertDialogFooter>
                                                          </AlertDialogContent>
                                                        </AlertDialog>
                                                      </div>
                                                    </td>
                                                  </tr>

                                                  {isOpen && (
                                                    <ProspectAsyncDependentRows
                                                      employeeId={Number((enr as any).employeeId)}
                                                      plan={plan}
                                                      enrollmentId={Number((enr as any).id)}
                                                    />
                                                  )}
                                                </React.Fragment>
                                              );
                                            }
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  {t === enrollmentCoverageTab && totalForTab > 0 && (
                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                      <div className="text-xs text-muted-foreground">
                                        Showing <span className="font-medium">{start}</span>-
                                        <span className="font-medium">{end}</span> of{" "}
                                        <span className="font-medium">{totalForTab}</span>
                                      </div>
                                      <div className="flex items-center gap-2 justify-end">
                                        <Select
                                          value={String(enrollmentPageSize)}
                                          onValueChange={(v) => handlePageSizeChange(v)}
                                        >
                                          <SelectTrigger className="h-8 w-[110px]">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="25">25 / page</SelectItem>
                                            <SelectItem value="50">50 / page</SelectItem>
                                            <SelectItem value="100">100 / page</SelectItem>
                                            <SelectItem value="200">200 / page</SelectItem>
                                          </SelectContent>
                                        </Select>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setCurrentPage(clampedPage - 1)}
                                          disabled={clampedPage <= 1}
                                        >
                                          Prev
                                        </Button>
                                        <div className="text-xs text-muted-foreground min-w-[90px] text-center">
                                          Page{" "}
                                          <span className="font-medium">{clampedPage}</span> of{" "}
                                          <span className="font-medium">{totalPages}</span>
                                        </div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() => setCurrentPage(clampedPage + 1)}
                                          disabled={clampedPage >= totalPages}
                                        >
                                          Next
                                        </Button>
                                      </div>
                                    </div>
                                  )}

                                  {t === enrollmentCoverageTab && (
                                    <div className="pt-6 border-t border-dashed">
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <User className="h-4 w-4" />
                                        <span>
                                          Employees Who Waived {label(enrollmentCoverageTab).toLowerCase()} Coverage
                                        </span>
                                        <Badge variant="secondary" className="h-5 px-2">
                                          {waivedEmployeesForTab.length}
                                        </Badge>
                                      </div>

                                      <div className="mt-3">
                                        {waivedEmployeesForTab.length === 0 ? (
                                          <div className="text-sm text-muted-foreground italic">
                                            No waived employees
                                          </div>
                                        ) : (
                                          <div className="space-y-2">
                                            {(waivedEmployeesForTab as any[]).map((w: any) => (
                                              <div
                                                key={`waived-${w.employeeId}`}
                                                className="border rounded-lg p-3 flex items-center justify-between gap-4 max-w-[520px]"
                                              >
                                                <div className="flex items-center gap-3 min-w-0">
                                                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center">
                                                    <User className="h-4 w-4 text-muted-foreground" />
                                                  </div>
                                                  <div className="min-w-0">
                                                    <div className="font-medium truncate">
                                                      {w.employee
                                                        ? `${w.employee.firstName} ${w.employee.lastName}`
                                                        : `#${w.employeeId}`}
                                                    </div>
                                                    <div className="text-xs text-muted-foreground truncate">
                                                      {w.employee?.department || ""}
                                                    </div>
                                                  </div>
                                                </div>
                                                <div className="text-right">
                                                  <Badge variant="secondary" className="h-6 px-2">
                                                    Waived
                                                  </Badge>
                                                  <div className="text-xs text-muted-foreground mt-1 max-w-[260px]">
                                                    {w.waiverReason ? w.waiverReason : "\u00A0"}
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>

                                      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground pt-4">
                                        <div>Participation Rate: {participationRateForTab}%</div>
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          disabled={waivedEmployeesForTab.length === 0}
                                        >
                                          Export Waiver List
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </CardContent>
                              </Card>
                            </TabsContent>
                          ))}
                        </Tabs>
                      );
                    })()}
                  </div>
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <div className="rounded-full bg-muted p-4 mb-4">
                        <Users className="h-8 w-8 text-muted-foreground" />
                      </div>
                      <h3 className="text-lg font-semibold mb-2">No enrollments yet</h3>
                      <p className="text-sm text-muted-foreground text-center mb-6 max-w-sm">
                        Create the first enrollment to see it listed here. Enrollments track employee benefit selections and coverage.
                      </p>
                      <Button onClick={() => setIsEnrollmentDialogOpen(true)} size="lg">
                        <Plus className="h-4 w-4 mr-2" />
                        Enroll Employees
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="open-enrollment" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Open Enrollment Planning</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose max-w-none">
                    <h4>Open Enrollment Preparation for Prospects:</h4>
                    <ol className="list-decimal list-inside space-y-2">
                      <li>
                        Present comprehensive benefit options during proposal
                        phase
                      </li>
                      <li>
                        Analyze prospect's current benefit offerings and pain
                        points
                      </li>
                      <li>
                        Evaluate plan options that meet their budget and
                        employee needs
                      </li>
                      <li>
                        Set implementation timeline and communication schedule
                      </li>
                      <li>
                        Prepare enrollment materials and decision support tools
                      </li>
                      <li>
                        Plan transition from current benefits to new offerings
                      </li>
                    </ol>

                    <h4 className="mt-6">Key Planning Dates:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Benefits review and selection: TBD</li>
                      <li>Open enrollment communication launch: TBD</li>
                      <li>Employee decision deadline: TBD</li>
                      <li>New coverage effective date: TBD</li>
                    </ul>

                    <h4 className="mt-6">Next Steps:</h4>
                    <p>
                      Work with your benefits consultant to develop a
                      comprehensive benefits strategy that meets your employees'
                      needs and your budget requirements.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="instructions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Benefits Implementation Guide</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="prose max-w-none">
                    <h4>Getting Started with Benefits:</h4>
                    <ol className="list-decimal list-inside space-y-2">
                      <li>
                        Define your benefits objectives and budget parameters
                      </li>
                      <li>
                        Survey employees to understand their needs and
                        preferences
                      </li>
                      <li>
                        Review available plan options with your benefits
                        consultant
                      </li>
                      <li>
                        Select plans that balance cost, coverage, and employee
                        value
                      </li>
                      <li>Develop communication strategy for plan rollout</li>
                      <li>Set up enrollment systems and support processes</li>
                    </ol>

                    <h4 className="mt-6">Implementation Timeline:</h4>
                    <ul className="list-disc list-inside space-y-1">
                      <li>
                        Benefits planning and selection: 90-120 days before
                        effective date
                      </li>
                      <li>
                        Enrollment system setup: 60-90 days before open
                        enrollment
                      </li>
                      <li>
                        Employee communication launch: 30-45 days before
                        enrollment
                      </li>
                      <li>Open enrollment period: 2-4 weeks</li>
                    </ul>

                    <h4 className="mt-6">Support Resources:</h4>
                    <p>
                      Your dedicated benefits consultant will guide you through
                      each step of the process and provide ongoing support for
                      plan administration and employee questions.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="quotes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Quotes & Proposals</CardTitle>
              <Button
                onClick={() =>
                  setLocation(
                    `/quoting?wizard=true&mode=quote&step=1&prospect=${prospectId}`
                  )
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Quote
              </Button>
            </CardHeader>
            <CardContent>
              {quotesLoading ? (
                <div>Loading quotes...</div>
              ) : quotes && quotes.length > 0 ? (
                <div className="space-y-4">
                  {quotes.map((quote: any) => (
                    <div key={quote.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{quote.companyName}</h4>
                          <p className="text-sm text-gray-600">
                            Plan Type: {quote.planType}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            {quote.monthlyPremium
                              ? `$${quote.monthlyPremium}/month`
                              : "Premium TBD"}
                          </p>
                          <Badge className={getStatusColor(quote.status)}>
                            {quote.status}
                          </Badge>
                          <p className="text-sm text-gray-600 mt-1">
                            Valid until:{" "}
                            {quote.validUntil
                              ? formatDate(quote.validUntil)
                              : "TBD"}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <DollarSign className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No quotes or proposals generated yet</p>
                  <p className="text-sm">
                    Create proposals to track potential business with this
                    prospect
                  </p>
                  <Button
                    className="mt-4"
                    onClick={() =>
                      setLocation(
                        `/quoting?wizard=true&mode=quote&step=1&prospect=${prospectId}`
                      )
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Create Quote
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="space-y-6">
          <ProspectDocumentsTab prospectId={prospectId} />
        </TabsContent>

        <TabsContent value="contact-history">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Contact History
              </CardTitle>
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Interaction
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Add Contact Interaction</DialogTitle>
                    <DialogDescription>
                      Record a new communication with this prospect to track
                      your sales progress.
                    </DialogDescription>
                  </DialogHeader>
                  <Form {...form}>
                    <form
                      onSubmit={form.handleSubmit((data) =>
                        createInteractionMutation.mutate(data)
                      )}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="type"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Interaction Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="email">Email</SelectItem>
                                  <SelectItem value="phone">
                                    Phone Call
                                  </SelectItem>
                                  <SelectItem value="meeting">
                                    Meeting
                                  </SelectItem>
                                  <SelectItem value="proposal">
                                    Proposal Sent
                                  </SelectItem>
                                  <SelectItem value="follow-up">
                                    Follow-up
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="contactDate"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Contact Date</FormLabel>
                              <FormControl>
                                <Input
                                  type="datetime-local"
                                  {...field}
                                  value={field.value || ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <FormField
                        control={form.control}
                        name="subject"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Subject</FormLabel>
                            <FormControl>
                              <Input
                                placeholder="Brief description of the interaction"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Notes</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Detailed notes about the interaction..."
                                rows={4}
                                {...field}
                                value={field.value || ""}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="outcome"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Outcome</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select outcome" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="successful">
                                    Successful
                                  </SelectItem>
                                  <SelectItem value="needs_follow_up">
                                    Needs Follow-up
                                  </SelectItem>
                                  <SelectItem value="no_response">
                                    No Response
                                  </SelectItem>
                                  <SelectItem value="resolved">
                                    Resolved
                                  </SelectItem>
                                  <SelectItem value="escalated">
                                    Escalated
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="nextFollowUp"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Next Follow-up (Optional)</FormLabel>
                              <FormControl>
                                <Input
                                  type="date"
                                  {...field}
                                  value={field.value || ""}
                                  onChange={(e) =>
                                    field.onChange(e.target.value)
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button
                          type="submit"
                          disabled={createInteractionMutation.isPending}
                        >
                          {createInteractionMutation.isPending
                            ? "Adding..."
                            : "Add Interaction"}
                        </Button>
                      </div>
                    </form>
                  </Form>
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent>
              {interactionsLoading ? (
                <div>Loading contact history...</div>
              ) : (
                <div>
                  <div className="text-sm text-gray-500 mb-2">
                    Found {contactInteractions?.length || 0} interactions
                  </div>
                  {contactInteractions && contactInteractions.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Outcome</TableHead>
                          <TableHead>Next Follow-up</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contactInteractions.map((interaction: any) => (
                          <TableRow key={interaction.id}>
                            <TableCell>
                              {formatDate(interaction.contactDate)}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {interaction.type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-medium">
                                  {interaction.subject}
                                </div>
                                {interaction.notes && (
                                  <div className="text-sm text-gray-600 mt-1">
                                    {interaction.notes}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {OUTCOME_LABELS[interaction.outcome as any] ||
                                "-"}
                            </TableCell>
                            <TableCell>
                              {interaction.nextFollowUp
                                ? formatDateLong(
                                    interaction.nextFollowUp as any
                                  )
                                : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <History className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No contact interactions recorded yet</p>
                      <p className="text-sm">
                        Start tracking communications to build a complete
                        prospect history
                      </p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Edit Prospect Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Prospect</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form
              onSubmit={editForm.handleSubmit((data) => {
                // Convert data types for server
                const processedData = {
                  ...data,
                  address: composeAddressLine(data) || data.address || "",
                  expectedValue: data.expectedValue
                    ? String(data.expectedValue)
                    : null,
                  expectedCloseDate: data.expectedCloseDate
                    ? parseDateLocal(data.expectedCloseDate) ?? new Date(data.expectedCloseDate)
                    : null,
                  lastContactDate: data.lastContactDate
                    ? parseDateLocal(data.lastContactDate) ?? new Date(data.lastContactDate)
                    : null,
                };

                updateProspectMutation.mutate(processedData);
              })}
              className="space-y-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter company name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="primaryContactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Contact Name</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value || ""}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select primary contact" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">No primary contact</SelectItem>
                          {sortedEmployees
                            .filter((emp) => emp.prospectId === prospectId)
                            .map((employee) => (
                              <SelectItem
                                key={employee.id}
                                value={`${employee.firstName} ${employee.lastName}`}
                              >
                                {employee.firstName} {employee.lastName}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="Enter contact email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="contactPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contact Phone</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter contact phone"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="website"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Website</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="https://example.com"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <AddressFields control={editForm.control} />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="ein"
                  render={({ field }) => {
                    const handleEINChange = (event: ChangeEvent<HTMLInputElement>) => {
                      const formattedValue = formatEINInputValue(event.target.value);
                      field.onChange(formattedValue);
                    };

                    return (
                      <FormItem>
                        <FormLabel>EIN (Employer Identification Number)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="XX-XXXXXXX"
                            {...field}
                            value={field.value || ""}
                            onChange={handleEINChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={editForm.control}
                  name="sicCodeId"
                  render={({ field }) => {
                    const selected = sicOptions.find(
                      (opt) => opt.id === field.value
                    );
                    const label = selected
                      ? `${formatSicCode(selected.code)} - ${selected.industryTitle}`
                      : "Select SIC code";
                    return (
                      <FormItem className="max-w-full">
                        <FormLabel>SIC Code</FormLabel>
                        <div className="w-full overflow-hidden">
                          <Popover open={sicOpen} onOpenChange={setSicOpen}>
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={sicOpen}
                                className="w-full flex items-center justify-between overflow-hidden"
                              >
                                <span className="truncate flex-1 text-left">{label}</span>
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-[--radix-popover-trigger-width] p-0"
                              onWheel={(e) => e.stopPropagation()}
                            >
                              <Command>
                                <CommandInput placeholder="Search code or industry" />
                                <CommandEmpty>No results found.</CommandEmpty>
                                <CommandList style={{ maxHeight: '250px', overflowY: 'auto' }}>
                                  <CommandGroup>
                                    {sicOptions.map((opt) => (
                                      <CommandItem
                                        key={opt.id}
                                        value={`${formatSicCode(opt.code)} ${opt.industryTitle}`}
                                        onSelect={() => {
                                          field.onChange(opt.id);
                                          setSicOpen(false);
                                        }}
                                      >
                                        <Check
                                          className={cn(
                                            "mr-2 h-4 w-4",
                                            opt.id === field.value
                                              ? "opacity-100"
                                              : "opacity-0"
                                          )}
                                        />
                                        {formatSicCode(opt.code)} - {opt.industryTitle}
                                      </CommandItem>
                                    ))}
                                  </CommandGroup>
                                </CommandList>
                              </Command>
                            </PopoverContent>
                          </Popover>
                        </div>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={editForm.control}
                  name="entityType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Entity Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select entity type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="corporation">Corporation</SelectItem>
                          <SelectItem value="c-corporation">
                            C Corporation
                          </SelectItem>
                          <SelectItem value="s-corporation">
                            S Corporation
                          </SelectItem>
                          <SelectItem value="llc">LLC</SelectItem>
                          <SelectItem value="llp">LLP</SelectItem>
                          <SelectItem value="partnership">Partnership</SelectItem>
                          <SelectItem value="sole-proprietor">
                            Sole Proprietor
                          </SelectItem>
                          <SelectItem value="non-profit">Non-Profit</SelectItem>
                          <SelectItem value="government-agency">
                            Government Agency
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={editForm.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="lead">Lead</SelectItem>
                          <SelectItem value="qualified">Qualified</SelectItem>
                          <SelectItem value="proposal_sent">
                            Proposal Sent
                          </SelectItem>
                          <SelectItem value="negotiating">
                            Negotiating
                          </SelectItem>
                          <SelectItem value="closed_won">Closed Won</SelectItem>
                          <SelectItem value="closed_lost">
                            Closed Lost
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select priority" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editForm.control}
                  name="source"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Source</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select source" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(SOURCE_OPTIONS).map(
                            ([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            )
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <FormField
                  control={editForm.control}
                  name="expectedValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expected Value</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="25000.00"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="expectedCloseDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Expected Close Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editForm.control}
                  name="lastContactDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Contact Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={editForm.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notes</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Additional notes about this prospect..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateProspectMutation.isPending}
                >
                  {updateProspectMutation.isPending
                    ? "Updating..."
                    : "Update Prospect"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog
        open={isEditEmployeeDialogOpen}
        onOpenChange={setIsEditEmployeeDialogOpen}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          <Form {...editEmployeeForm}>
            <form
              onSubmit={editEmployeeForm.handleSubmit((data) =>
                updateEmployeeMutation.mutate(data)
              )}
              className="space-y-4"
            >
              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={editEmployeeForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="John" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john.doe@company.com"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="555-123-4567" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editEmployeeForm.control}
                  name="departmentId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Department</FormLabel>
                      <FormControl>
                        <OrganizationSelect
                          orgType="department"
                          ownerType="prospect"
                          ownerId={prospectId}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select Department"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="jobTitleId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <OrganizationSelect
                          orgType="job-title"
                          ownerType="prospect"
                          ownerId={prospectId}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select Job Title"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={editEmployeeForm.control}
                  name="locationId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Location</FormLabel>
                      <FormControl>
                        <OrganizationSelect
                          orgType="location"
                          ownerType="prospect"
                          ownerId={prospectId}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select Location"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="employeeClassId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employee Class</FormLabel>
                      <FormControl>
                        <OrganizationSelect
                          orgType="employee-class"
                          ownerType="prospect"
                          ownerId={prospectId}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select Employee Class"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4">
                <FormField
                  control={editEmployeeForm.control}
                  name="salary"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Salary</FormLabel>
                      <FormControl>
                        <Input placeholder="75000" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <FormField
                  control={editEmployeeForm.control}
                  name="hireDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Hire Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of birth</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="emergencyContact"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emergency Contact</FormLabel>
                      <FormControl>
                        <Input placeholder="Jane Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editEmployeeForm.control}
                  name="emergencyPhone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Emergency Phone</FormLabel>
                      <FormControl>
                        <Input placeholder="555-987-6543" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Address Fields */}
              <AddressFields control={editEmployeeForm.control} />

              <div className="flex justify-end space-x-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditEmployeeDialogOpen(false);
                    setSelectedEmployee(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateEmployeeMutation.isPending}
                >
                  {updateEmployeeMutation.isPending
                    ? "Updating..."
                    : "Update Employee"}
                </Button>
              </div>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Import Employees Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Import Employees from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file with employee data. Download the template to see the required format.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Template Download Section */}
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Download the template to see the required format for importing employees.
              </p>
              <Button
                variant="outline"
                onClick={() => downloadEmployeeImportTemplate()}
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Import Template
              </Button>
            </div>

            {/* File Upload Section */}
            <div className="space-y-4 border-t pt-4">
              {!csvFile && (
                <div className="flex items-center justify-center w-full">
                  <label
                    htmlFor="csv-upload"
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-5">
                      <FileUp className="w-8 h-8 mb-4 text-gray-500" />
                      <p className="text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span>{" "}
                        your CSV file
                      </p>
                    </div>
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      className="hidden"
                      onChange={handleFileUpload}
                    />
                  </label>
                </div>
              )}

              {csvFile && (
                <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg relative">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <span className="text-sm font-medium">{csvFile.name}</span>
                  <span className="text-xs text-gray-500">
                    ({csvData.length} rows)
                  </span>
                  <button
                    className="absolute top-1/2 right-2 transform -translate-y-1/2 text-red-600 hover:text-red-800 p-1"
                    onClick={() => {
                      const fileInput = document.getElementById(
                        "csv-upload"
                      ) as HTMLInputElement;
                      if (fileInput) fileInput.value = ""; // Reset file input
                      setCsvFile(null);
                      setCsvData([]);
                      setImportPreview([]);
                    }}
                  >
                    <X className="h-4 w-4" color="black" />
                  </button>
                </div>
              )}
            </div>

            {/* Preview Section */}
            {isProcessingCsv && (
              <div className="flex items-center justify-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2">Processing CSV...</span>
              </div>
            )}

            {importPreview.length > 0 && (
              <div className="space-y-4">
                <h4 className="font-medium">Preview (First 5 rows)</h4>
                <div className="overflow-x-auto border rounded-lg dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          First Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Last Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Email
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Department
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Position
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Hire Date
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                      {importPreview.map((row, index) => (
                        <tr key={index}>
                          <td className="px-4 py-2 text-sm">
                            {row.firstname || row.firstName || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row.lastname || row.lastName || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row.email || row.Email || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row.department || row.Department || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row.position || row.Position || "-"}
                          </td>
                          <td className="px-4 py-2 text-sm">
                            {row.hireDate || row.hiredate || row.HireDate || "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="text-sm text-gray-600">
                  <p>
                    <strong>Total rows to import:</strong> {csvData.length}{" "}
                    <strong className="ml-2">Valid employees:</strong>{" "}
                    {
                      csvData.filter(
                        (row) => (row.firstname || row.firstName) && (row.lastname || row.lastName)
                      ).length
                    }
                  </p>
                  {csvData.length >
                    csvData.filter(
                      (row) => (row.firstname || row.firstName) && (row.lastname || row.lastName)
                    ).length && (
                    <p className="text-yellow-600">
                      <strong>Note:</strong>{" "}
                      {csvData.length -
                        csvData.filter(
                          (row) => (row.firstname || row.firstName) && (row.lastname || row.lastName)
                        ).length}{" "}
                      rows will be skipped (missing required fields: firstName, lastName)
                    </p>
                  )}
                  {csvData.some((row) => ((row.firstname || row.firstName) && (row.lastname || row.lastName) && !(row.email || row.Email))) && (
                    <p className="text-blue-600 mt-1">
                      <strong>Info:</strong> Some employees are missing email addresses. They will be imported without login credentials.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsImportDialogOpen(false);
                setCsvFile(null);
                setCsvData([]);
                setImportPreview([]);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleImportEmployees}
              disabled={!csvFile || bulkImportEmployeesMutation.isPending}
            >
              {bulkImportEmployeesMutation.isPending
                ? "Importing..."
                : "Import Employees"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Organization Items Approval Dialog */}
      <Dialog open={isOrgApprovalOpen} onOpenChange={setIsOrgApprovalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Missing Organization Items</DialogTitle>
            <DialogDescription>
              The following organization items need to be created before importing employees.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {pendingOrgItems.jobTitles.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Job Titles to Create</h4>
                <ul className="space-y-1 bg-gray-50 p-3 rounded-lg border">
                  {pendingOrgItems.jobTitles.map((title) => (
                    <li key={title} className="text-sm flex items-center">
                      <span className="inline-block w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {title}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pendingOrgItems.departments.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Departments to Create</h4>
                <ul className="space-y-1 bg-gray-50 p-3 rounded-lg border">
                  {pendingOrgItems.departments.map((dept) => (
                    <li key={dept} className="text-sm flex items-center">
                      <span className="inline-block w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      {dept}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pendingOrgItems.locations.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Locations to Create</h4>
                <ul className="space-y-1 bg-gray-50 p-3 rounded-lg border">
                  {pendingOrgItems.locations.map((location) => (
                    <li key={location} className="text-sm flex items-center">
                      <span className="inline-block w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                      {location}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pendingOrgItems.employeeClasses.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Employee Classes to Create</h4>
                <ul className="space-y-1 bg-gray-50 p-3 rounded-lg border">
                  {pendingOrgItems.employeeClasses.map((employeeClass) => (
                    <li key={employeeClass} className="text-sm flex items-center">
                      <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                      {employeeClass}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {pendingOrgItems.divisions.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Divisions to Create</h4>
                <ul className="space-y-1 bg-gray-50 p-3 rounded-lg border">
                  {pendingOrgItems.divisions.map((division) => (
                    <li key={division} className="text-sm flex items-center">
                      <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                      {division}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-900">
                These items will be created in your organization, and then the import will continue automatically.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsOrgApprovalOpen(false);
                setPendingOrgItems({ jobTitles: [], departments: [], locations: [], employeeClasses: [], divisions: [] });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleApproveOrgItems}
              disabled={createOrgItemsMutation.isPending}
            >
              {createOrgItemsMutation.isPending
                ? "Creating..."
                : "Create and Continue Import"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Missing Data Summary Dialog */}
      <Dialog open={isMissingDataDialogOpen} onOpenChange={setIsMissingDataDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Complete - Skipped Employees</DialogTitle>
            <DialogDescription>
              {skippedEmployees.length} {skippedEmployees.length === 1 ? "employee was" : "employees were"} skipped due to missing required data.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
              <p className="text-sm text-yellow-900 font-medium mb-3">
                The following employees were not imported because they are missing required information:
              </p>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {skippedEmployees.map((emp) => (
                  <div key={emp.rowNumber} className="bg-white border rounded p-3 text-sm">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-base mb-1">{emp.name}</div>
                        <div className="text-gray-600 text-xs">
                          <span>Row {emp.rowNumber}</span>
                          {emp.email && (
                            <span className="ml-2">• {emp.email}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-3">
                        <span className="text-xs text-red-600 font-medium">
                          Missing: {emp.missingFields.join(", ")}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-900">
                Update your CSV file with the missing information and import again. You can download this list for reference.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleDownloadMissingData}
            >
              Download Skipped List
            </Button>
            <Button
              onClick={() => {
                setIsMissingDataDialogOpen(false);
                setSkippedEmployees([]);
              }}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isConvertConfirmOpen} onOpenChange={setIsConvertConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Convert prospect to client?</AlertDialogTitle>
            <AlertDialogDescription>
              This will create a new client record{prospect?.contactEmail ? <> and email a temporary password to <strong>{prospect.contactEmail}</strong></> : ""}, transfer any linked employees, and mark this prospect as closed-won. You can't undo this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isConverting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleConfirmConvert();
              }}
              disabled={isConverting}
            >
              {isConverting ? "Converting..." : "Convert"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
